/**
 * Generato automaticamente.
 * framework-cmp-domande
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type {
  AggiornaModuloRequest,
  AggiornaModuloResponse,
  AggiungiAllegatoResult,
  AllegatoRichiestaDTO,
  AvviaEBolloRequest,
  AvviaPagamentoOnlineRequest,
  CambiamentoStatoRequest,
  CampoEstraibileCSV,
  CollegamentoRichiestaDTO,
  Competenza,
  ComunicazioneAnteprimaDTO,
  ComunicazioneDTO,
  ComunicazioneFrontOfficeCreateRequest,
  CreatePagamentoOneriRequest,
  DelegaModulo,
  DeleteFrontRichiesteIdModuliIdSezioniIdFileCodiceParams,
  GetFrontRichiesteCsvParams,
  GetFrontRichiesteGeojsonParams,
  GetFrontRichiesteIdCollegamentiParams,
  GetFrontRichiesteIdComunicazioniParams,
  GetFrontRichiesteIdImmaginiIdParams,
  GetFrontRichiesteIdModuliIdSezioniIdFileCodiceParams,
  GetFrontRichiesteIdProcuratoreParams,
  GetFrontRichiesteIdTipiComunicazioneParams,
  GetFrontRichiesteParams,
  GetFrontRichiesteValoriSezioniProcedimentoId200Item,
  GetFrontRichiesteXlsxParams,
  InfoGestore,
  InfoProcura,
  MappaGeografica,
  OnereWithDettaglioDTO,
  PagamentoWithDettaglioDTO,
  PostFrontRichiesteIdDelegaBody,
  PostFrontRichiesteIdModuliIdSezioniIdFileCodiceBody,
  PostFrontRichiesteIdModuliIdSezioniIdFileCodiceParams,
  PostFrontRichiesteIdModuloIdDocumentoFirmaBodyOne,
  PostFrontRichiesteIdModuloIdDocumentoFirmaBodyTwo,
  RichiestaDTO,
  RichiestaModuloDTO,
  RispondiComunicazioneRequest,
  SchedulationResult,
  SubentroDTO,
  TipoComunicazioneDTO,
  UpdateAllegatoRichiestaDTO,
  UpdateDelegaModulo,
  UpdateFileFirmatoModuloRichiestaDTO,
  UpdateProcuratoreRequest,
  UpdateRichiestaDTO,
  UpdateStatoRichiestaRequest,
  UpdateUtenteRequest,
  Utente,
  WsProblema,
  WsRispostaPaginataListComunicazioneDTO,
  WsRispostaPaginataListRichiestaDTO,
} from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Dettaglio della richiesta, visibile solo da un utente che è compilatore e/o delagato della richiestas
 * @summary Dettaglio richiesta
 */
export const getFrontRichiesteId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<RichiestaDTO>(
    { url: `/framework-cmp-domande/richieste/${id}`, method: 'get', signal },
    options,
  );
};

export const getGetFrontRichiesteIdQueryKey = (id: string) =>
  [`/framework-cmp-domande/richieste/${id}`] as const;

export const getGetFrontRichiesteIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontRichiesteId>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetFrontRichiesteIdQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontRichiesteId>>
  > = ({ signal }) => getFrontRichiesteId(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetFrontRichiesteIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontRichiesteId>>
>;
export type GetFrontRichiesteIdQueryError = ErrorType<unknown>;

/**
 * @summary Dettaglio richiesta
 */
export const useGetFrontRichiesteId = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontRichiesteIdQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Modifica richiesta tramite id richiesta
 * @summary Modifica richiesta
 */
export const putFrontRichiesteId = (
  id: string,
  updateRichiestaDTO: UpdateRichiestaDTO,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<RichiestaDTO>(
    {
      url: `/framework-cmp-domande/richieste/${id}`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: updateRichiestaDTO,
    },
    options,
  );
};

export const getPutFrontRichiesteIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putFrontRichiesteId>>,
    TError,
    { id: string; data: UpdateRichiestaDTO },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putFrontRichiesteId>>,
  TError,
  { id: string; data: UpdateRichiestaDTO },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putFrontRichiesteId>>,
    { id: string; data: UpdateRichiestaDTO }
  > = props => {
    const { id, data } = props ?? {};

    return putFrontRichiesteId(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutFrontRichiesteIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof putFrontRichiesteId>>
>;
export type PutFrontRichiesteIdMutationBody = UpdateRichiestaDTO;
export type PutFrontRichiesteIdMutationError = ErrorType<unknown>;

/**
 * @summary Modifica richiesta
 */
export const usePutFrontRichiesteId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putFrontRichiesteId>>,
    TError,
    { id: string; data: UpdateRichiestaDTO },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPutFrontRichiesteIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Cancella la richiesta se è in stato BOZZA e/o CREATA
 * @summary Cancellazione richiesta
 */
export const deleteFrontRichiesteId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<RichiestaDTO>(
    { url: `/framework-cmp-domande/richieste/${id}`, method: 'delete' },
    options,
  );
};

export const getDeleteFrontRichiesteIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteFrontRichiesteId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteFrontRichiesteId>>,
  TError,
  { id: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteFrontRichiesteId>>,
    { id: string }
  > = props => {
    const { id } = props ?? {};

    return deleteFrontRichiesteId(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteFrontRichiesteIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteFrontRichiesteId>>
>;

export type DeleteFrontRichiesteIdMutationError = ErrorType<unknown>;

/**
 * @summary Cancellazione richiesta
 */
export const useDeleteFrontRichiesteId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteFrontRichiesteId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getDeleteFrontRichiesteIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Modifica lo stato di una richiesta da BOZZA a PAGAMENTO o da PAGAMENTO a BOZZA
 * @summary Modifica lo stato di una richiesta da BOZZA a PAGAMENTO o da PAGAMENTO a BOZZA
 */
export const putFrontRichiesteIdStato = (
  id: string,
  updateStatoRichiestaRequest: UpdateStatoRichiestaRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    {
      url: `/framework-cmp-domande/richieste/${id}/stato`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: updateStatoRichiestaRequest,
    },
    options,
  );
};

export const getPutFrontRichiesteIdStatoMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putFrontRichiesteIdStato>>,
    TError,
    { id: string; data: UpdateStatoRichiestaRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putFrontRichiesteIdStato>>,
  TError,
  { id: string; data: UpdateStatoRichiestaRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putFrontRichiesteIdStato>>,
    { id: string; data: UpdateStatoRichiestaRequest }
  > = props => {
    const { id, data } = props ?? {};

    return putFrontRichiesteIdStato(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutFrontRichiesteIdStatoMutationResult = NonNullable<
  Awaited<ReturnType<typeof putFrontRichiesteIdStato>>
>;
export type PutFrontRichiesteIdStatoMutationBody = UpdateStatoRichiestaRequest;
export type PutFrontRichiesteIdStatoMutationError = ErrorType<unknown>;

/**
 * @summary Modifica lo stato di una richiesta da BOZZA a PAGAMENTO o da PAGAMENTO a BOZZA
 */
export const usePutFrontRichiesteIdStato = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putFrontRichiesteIdStato>>,
    TError,
    { id: string; data: UpdateStatoRichiestaRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPutFrontRichiesteIdStatoMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Aggiorna file firmato protocollato sul modulo della richiesta
 * @summary Aggiorna file firmato protocollato sul modulo della richiesta
 */
export const putFrontRichiesteIdModuliIdAggiornaFileFirmatoProtocollato = (
  id: string,
  idRichiestaModulo: string,
  updateFileFirmatoModuloRichiestaDTO: UpdateFileFirmatoModuloRichiestaDTO,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<RichiestaModuloDTO>(
    {
      url: `/framework-cmp-domande/richieste/${id}/moduli/${idRichiestaModulo}/aggiornaFileFirmatoProtocollato`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: updateFileFirmatoModuloRichiestaDTO,
    },
    options,
  );
};

export const getPutFrontRichiesteIdModuliIdAggiornaFileFirmatoProtocollatoMutationOptions =
  <TError = ErrorType<unknown>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<
          typeof putFrontRichiesteIdModuliIdAggiornaFileFirmatoProtocollato
        >
      >,
      TError,
      {
        id: string;
        idRichiestaModulo: string;
        data: UpdateFileFirmatoModuloRichiestaDTO;
      },
      TContext
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  }): UseMutationOptions<
    Awaited<
      ReturnType<
        typeof putFrontRichiesteIdModuliIdAggiornaFileFirmatoProtocollato
      >
    >,
    TError,
    {
      id: string;
      idRichiestaModulo: string;
      data: UpdateFileFirmatoModuloRichiestaDTO;
    },
    TContext
  > => {
    const { mutation: mutationOptions, request: requestOptions } =
      options ?? {};

    const mutationFn: MutationFunction<
      Awaited<
        ReturnType<
          typeof putFrontRichiesteIdModuliIdAggiornaFileFirmatoProtocollato
        >
      >,
      {
        id: string;
        idRichiestaModulo: string;
        data: UpdateFileFirmatoModuloRichiestaDTO;
      }
    > = props => {
      const { id, idRichiestaModulo, data } = props ?? {};

      return putFrontRichiesteIdModuliIdAggiornaFileFirmatoProtocollato(
        id,
        idRichiestaModulo,
        data,
        requestOptions,
      );
    };

    return { mutationFn, ...mutationOptions };
  };

export type PutFrontRichiesteIdModuliIdAggiornaFileFirmatoProtocollatoMutationResult =
  NonNullable<
    Awaited<
      ReturnType<
        typeof putFrontRichiesteIdModuliIdAggiornaFileFirmatoProtocollato
      >
    >
  >;
export type PutFrontRichiesteIdModuliIdAggiornaFileFirmatoProtocollatoMutationBody =
  UpdateFileFirmatoModuloRichiestaDTO;
export type PutFrontRichiesteIdModuliIdAggiornaFileFirmatoProtocollatoMutationError =
  ErrorType<unknown>;

/**
 * @summary Aggiorna file firmato protocollato sul modulo della richiesta
 */
export const usePutFrontRichiesteIdModuliIdAggiornaFileFirmatoProtocollato = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<
      ReturnType<
        typeof putFrontRichiesteIdModuliIdAggiornaFileFirmatoProtocollato
      >
    >,
    TError,
    {
      id: string;
      idRichiestaModulo: string;
      data: UpdateFileFirmatoModuloRichiestaDTO;
    },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPutFrontRichiesteIdModuliIdAggiornaFileFirmatoProtocollatoMutationOptions(
      options,
    );

  return useMutation(mutationOptions);
};
/**
 * Aggiorna file firmato protocollato sul modulo della richiesta
 * @summary Aggiorna file firmato protocollato sul modulo della richiesta
 */
export const putFrontRichiesteIdAllegatiIdAggiornaAllegatoProtocollato = (
  id: string,
  idAllegato: string,
  updateAllegatoRichiestaDTO: UpdateAllegatoRichiestaDTO,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<AllegatoRichiestaDTO>(
    {
      url: `/framework-cmp-domande/richieste/${id}/allegati/${idAllegato}/aggiornaAllegatoProtocollato`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: updateAllegatoRichiestaDTO,
    },
    options,
  );
};

export const getPutFrontRichiesteIdAllegatiIdAggiornaAllegatoProtocollatoMutationOptions =
  <TError = ErrorType<unknown>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<
          typeof putFrontRichiesteIdAllegatiIdAggiornaAllegatoProtocollato
        >
      >,
      TError,
      { id: string; idAllegato: string; data: UpdateAllegatoRichiestaDTO },
      TContext
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  }): UseMutationOptions<
    Awaited<
      ReturnType<
        typeof putFrontRichiesteIdAllegatiIdAggiornaAllegatoProtocollato
      >
    >,
    TError,
    { id: string; idAllegato: string; data: UpdateAllegatoRichiestaDTO },
    TContext
  > => {
    const { mutation: mutationOptions, request: requestOptions } =
      options ?? {};

    const mutationFn: MutationFunction<
      Awaited<
        ReturnType<
          typeof putFrontRichiesteIdAllegatiIdAggiornaAllegatoProtocollato
        >
      >,
      { id: string; idAllegato: string; data: UpdateAllegatoRichiestaDTO }
    > = props => {
      const { id, idAllegato, data } = props ?? {};

      return putFrontRichiesteIdAllegatiIdAggiornaAllegatoProtocollato(
        id,
        idAllegato,
        data,
        requestOptions,
      );
    };

    return { mutationFn, ...mutationOptions };
  };

export type PutFrontRichiesteIdAllegatiIdAggiornaAllegatoProtocollatoMutationResult =
  NonNullable<
    Awaited<
      ReturnType<
        typeof putFrontRichiesteIdAllegatiIdAggiornaAllegatoProtocollato
      >
    >
  >;
export type PutFrontRichiesteIdAllegatiIdAggiornaAllegatoProtocollatoMutationBody =
  UpdateAllegatoRichiestaDTO;
export type PutFrontRichiesteIdAllegatiIdAggiornaAllegatoProtocollatoMutationError =
  ErrorType<unknown>;

/**
 * @summary Aggiorna file firmato protocollato sul modulo della richiesta
 */
export const usePutFrontRichiesteIdAllegatiIdAggiornaAllegatoProtocollato = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<
      ReturnType<
        typeof putFrontRichiesteIdAllegatiIdAggiornaAllegatoProtocollato
      >
    >,
    TError,
    { id: string; idAllegato: string; data: UpdateAllegatoRichiestaDTO },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPutFrontRichiesteIdAllegatiIdAggiornaAllegatoProtocollatoMutationOptions(
      options,
    );

  return useMutation(mutationOptions);
};
/**
 * Aggiorna i dati del modulo e delle sue sezioni
 * @summary Aggiorna i dati del modulo e delle sue sezioni
 */
export const putFrontRichiesteIdModuliId = (
  idRichiesta: string,
  idRichiestaModulo: string,
  aggiornaModuloRequest: AggiornaModuloRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<AggiornaModuloResponse>(
    {
      url: `/framework-cmp-domande/richieste/${idRichiesta}/moduli/${idRichiestaModulo}`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: aggiornaModuloRequest,
    },
    options,
  );
};

export const getPutFrontRichiesteIdModuliIdMutationOptions = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putFrontRichiesteIdModuliId>>,
    TError,
    {
      idRichiesta: string;
      idRichiestaModulo: string;
      data: AggiornaModuloRequest;
    },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putFrontRichiesteIdModuliId>>,
  TError,
  {
    idRichiesta: string;
    idRichiestaModulo: string;
    data: AggiornaModuloRequest;
  },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putFrontRichiesteIdModuliId>>,
    {
      idRichiesta: string;
      idRichiestaModulo: string;
      data: AggiornaModuloRequest;
    }
  > = props => {
    const { idRichiesta, idRichiestaModulo, data } = props ?? {};

    return putFrontRichiesteIdModuliId(
      idRichiesta,
      idRichiestaModulo,
      data,
      requestOptions,
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type PutFrontRichiesteIdModuliIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof putFrontRichiesteIdModuliId>>
>;
export type PutFrontRichiesteIdModuliIdMutationBody = AggiornaModuloRequest;
export type PutFrontRichiesteIdModuliIdMutationError = ErrorType<WsProblema>;

/**
 * @summary Aggiorna i dati del modulo e delle sue sezioni
 */
export const usePutFrontRichiesteIdModuliId = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putFrontRichiesteIdModuliId>>,
    TError,
    {
      idRichiesta: string;
      idRichiestaModulo: string;
      data: AggiornaModuloRequest;
    },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPutFrontRichiesteIdModuliIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Invia una votazione
 * @summary Invia una votazione
 */
export const postFrontRichiesteIdVotazione = (
  id: string,
  aggiornaModuloRequest: AggiornaModuloRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    {
      url: `/framework-cmp-domande/richieste/${id}/votazione`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: aggiornaModuloRequest,
    },
    options,
  );
};

export const getPostFrontRichiesteIdVotazioneMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontRichiesteIdVotazione>>,
    TError,
    { id: string; data: AggiornaModuloRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postFrontRichiesteIdVotazione>>,
  TError,
  { id: string; data: AggiornaModuloRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postFrontRichiesteIdVotazione>>,
    { id: string; data: AggiornaModuloRequest }
  > = props => {
    const { id, data } = props ?? {};

    return postFrontRichiesteIdVotazione(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostFrontRichiesteIdVotazioneMutationResult = NonNullable<
  Awaited<ReturnType<typeof postFrontRichiesteIdVotazione>>
>;
export type PostFrontRichiesteIdVotazioneMutationBody = AggiornaModuloRequest;
export type PostFrontRichiesteIdVotazioneMutationError = ErrorType<unknown>;

/**
 * @summary Invia una votazione
 */
export const usePostFrontRichiesteIdVotazione = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontRichiesteIdVotazione>>,
    TError,
    { id: string; data: AggiornaModuloRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostFrontRichiesteIdVotazioneMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Informazioni sul procuratore della richiesta
 * @summary Informazioni sul procuratore
 */
export const getFrontRichiesteIdProcuratore = (
  id: string,
  params?: GetFrontRichiesteIdProcuratoreParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<InfoProcura>(
    {
      url: `/framework-cmp-domande/richieste/${id}/procuratore`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetFrontRichiesteIdProcuratoreQueryKey = (
  id: string,
  params?: GetFrontRichiesteIdProcuratoreParams,
) =>
  [
    `/framework-cmp-domande/richieste/${id}/procuratore`,
    ...(params ? [params] : []),
  ] as const;

export const getGetFrontRichiesteIdProcuratoreQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteIdProcuratore>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  params?: GetFrontRichiesteIdProcuratoreParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteIdProcuratore>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontRichiesteIdProcuratore>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetFrontRichiesteIdProcuratoreQueryKey(id, params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontRichiesteIdProcuratore>>
  > = ({ signal }) =>
    getFrontRichiesteIdProcuratore(id, params, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetFrontRichiesteIdProcuratoreQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontRichiesteIdProcuratore>>
>;
export type GetFrontRichiesteIdProcuratoreQueryError = ErrorType<unknown>;

/**
 * @summary Informazioni sul procuratore
 */
export const useGetFrontRichiesteIdProcuratore = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteIdProcuratore>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  params?: GetFrontRichiesteIdProcuratoreParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteIdProcuratore>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontRichiesteIdProcuratoreQueryOptions(
    id,
    params,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Aggiorna il procuratore della richiesta
 * @summary Aggiorna il procuratore
 */
export const postFrontRichiesteIdProcuratore = (
  id: string,
  updateProcuratoreRequest: UpdateProcuratoreRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<InfoProcura>(
    {
      url: `/framework-cmp-domande/richieste/${id}/procuratore`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: updateProcuratoreRequest,
    },
    options,
  );
};

export const getPostFrontRichiesteIdProcuratoreMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontRichiesteIdProcuratore>>,
    TError,
    { id: string; data: UpdateProcuratoreRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postFrontRichiesteIdProcuratore>>,
  TError,
  { id: string; data: UpdateProcuratoreRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postFrontRichiesteIdProcuratore>>,
    { id: string; data: UpdateProcuratoreRequest }
  > = props => {
    const { id, data } = props ?? {};

    return postFrontRichiesteIdProcuratore(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostFrontRichiesteIdProcuratoreMutationResult = NonNullable<
  Awaited<ReturnType<typeof postFrontRichiesteIdProcuratore>>
>;
export type PostFrontRichiesteIdProcuratoreMutationBody =
  UpdateProcuratoreRequest;
export type PostFrontRichiesteIdProcuratoreMutationError = ErrorType<unknown>;

/**
 * @summary Aggiorna il procuratore
 */
export const usePostFrontRichiesteIdProcuratore = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontRichiesteIdProcuratore>>,
    TError,
    { id: string; data: UpdateProcuratoreRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostFrontRichiesteIdProcuratoreMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Rimuove il procuratore della richiesta
 * @summary Rimuove il procuratore
 */
export const deleteFrontRichiesteIdProcuratore = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    {
      url: `/framework-cmp-domande/richieste/${id}/procuratore`,
      method: 'delete',
    },
    options,
  );
};

export const getDeleteFrontRichiesteIdProcuratoreMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteFrontRichiesteIdProcuratore>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteFrontRichiesteIdProcuratore>>,
  TError,
  { id: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteFrontRichiesteIdProcuratore>>,
    { id: string }
  > = props => {
    const { id } = props ?? {};

    return deleteFrontRichiesteIdProcuratore(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteFrontRichiesteIdProcuratoreMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteFrontRichiesteIdProcuratore>>
>;

export type DeleteFrontRichiesteIdProcuratoreMutationError = ErrorType<unknown>;

/**
 * @summary Rimuove il procuratore
 */
export const useDeleteFrontRichiesteIdProcuratore = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteFrontRichiesteIdProcuratore>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getDeleteFrontRichiesteIdProcuratoreMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Restituisce l’elenco di tutti i pagamenti associati a una domanda
 * @summary Recupero elenco pagamenti associati ad una domanda
 */
export const getFrontRichiesteIdPagamenti = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<PagamentoWithDettaglioDTO[]>(
    {
      url: `/framework-cmp-domande/richieste/${id}/pagamenti`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetFrontRichiesteIdPagamentiQueryKey = (id: string) =>
  [`/framework-cmp-domande/richieste/${id}/pagamenti`] as const;

export const getGetFrontRichiesteIdPagamentiQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteIdPagamenti>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteIdPagamenti>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontRichiesteIdPagamenti>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetFrontRichiesteIdPagamentiQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontRichiesteIdPagamenti>>
  > = ({ signal }) => getFrontRichiesteIdPagamenti(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetFrontRichiesteIdPagamentiQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontRichiesteIdPagamenti>>
>;
export type GetFrontRichiesteIdPagamentiQueryError = ErrorType<unknown>;

/**
 * @summary Recupero elenco pagamenti associati ad una domanda
 */
export const useGetFrontRichiesteIdPagamenti = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteIdPagamenti>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteIdPagamenti>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontRichiesteIdPagamentiQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * A partire da un insieme di oneri selezionati manualmente dall'utente, crea in automatico i pagamenti (pacchetti di oneri )per il pagamento
 * @summary Crea un pagamento da effettuare e associa a questo dei pacchetti di oneri
 */
export const postFrontRichiesteIdPagamenti = (
  id: string,
  createPagamentoOneriRequest: CreatePagamentoOneriRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    {
      url: `/framework-cmp-domande/richieste/${id}/pagamenti`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: createPagamentoOneriRequest,
    },
    options,
  );
};

export const getPostFrontRichiesteIdPagamentiMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontRichiesteIdPagamenti>>,
    TError,
    { id: string; data: CreatePagamentoOneriRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postFrontRichiesteIdPagamenti>>,
  TError,
  { id: string; data: CreatePagamentoOneriRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postFrontRichiesteIdPagamenti>>,
    { id: string; data: CreatePagamentoOneriRequest }
  > = props => {
    const { id, data } = props ?? {};

    return postFrontRichiesteIdPagamenti(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostFrontRichiesteIdPagamentiMutationResult = NonNullable<
  Awaited<ReturnType<typeof postFrontRichiesteIdPagamenti>>
>;
export type PostFrontRichiesteIdPagamentiMutationBody =
  CreatePagamentoOneriRequest;
export type PostFrontRichiesteIdPagamentiMutationError = ErrorType<unknown>;

/**
 * @summary Crea un pagamento da effettuare e associa a questo dei pacchetti di oneri
 */
export const usePostFrontRichiesteIdPagamenti = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontRichiesteIdPagamenti>>,
    TError,
    { id: string; data: CreatePagamentoOneriRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostFrontRichiesteIdPagamentiMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Avvia una transazione per il pagamento online (Modello 1) spontaneo e restituisce l’URL verso il quale effettuare il redirect lato frontend
 * @summary avvia un pagamento online (Modello 1)
 */
export const postFrontRichiesteIdPagamentiIdPagamentoOnline = (
  id: string,
  idPagamento: string,
  avviaPagamentoOnlineRequest: AvviaPagamentoOnlineRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<string>(
    {
      url: `/framework-cmp-domande/richieste/${id}/pagamenti/${idPagamento}/pagamento-online`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: avviaPagamentoOnlineRequest,
    },
    options,
  );
};

export const getPostFrontRichiesteIdPagamentiIdPagamentoOnlineMutationOptions =
  <TError = ErrorType<unknown>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<typeof postFrontRichiesteIdPagamentiIdPagamentoOnline>
      >,
      TError,
      { id: string; idPagamento: string; data: AvviaPagamentoOnlineRequest },
      TContext
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  }): UseMutationOptions<
    Awaited<ReturnType<typeof postFrontRichiesteIdPagamentiIdPagamentoOnline>>,
    TError,
    { id: string; idPagamento: string; data: AvviaPagamentoOnlineRequest },
    TContext
  > => {
    const { mutation: mutationOptions, request: requestOptions } =
      options ?? {};

    const mutationFn: MutationFunction<
      Awaited<
        ReturnType<typeof postFrontRichiesteIdPagamentiIdPagamentoOnline>
      >,
      { id: string; idPagamento: string; data: AvviaPagamentoOnlineRequest }
    > = props => {
      const { id, idPagamento, data } = props ?? {};

      return postFrontRichiesteIdPagamentiIdPagamentoOnline(
        id,
        idPagamento,
        data,
        requestOptions,
      );
    };

    return { mutationFn, ...mutationOptions };
  };

export type PostFrontRichiesteIdPagamentiIdPagamentoOnlineMutationResult =
  NonNullable<
    Awaited<ReturnType<typeof postFrontRichiesteIdPagamentiIdPagamentoOnline>>
  >;
export type PostFrontRichiesteIdPagamentiIdPagamentoOnlineMutationBody =
  AvviaPagamentoOnlineRequest;
export type PostFrontRichiesteIdPagamentiIdPagamentoOnlineMutationError =
  ErrorType<unknown>;

/**
 * @summary avvia un pagamento online (Modello 1)
 */
export const usePostFrontRichiesteIdPagamentiIdPagamentoOnline = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontRichiesteIdPagamentiIdPagamentoOnline>>,
    TError,
    { id: string; idPagamento: string; data: AvviaPagamentoOnlineRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostFrontRichiesteIdPagamentiIdPagamentoOnlineMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Avvia un pagamento per il pagamento tramite marca da bollo digitale (Modello e-bollo) e restituisce il RID relativo alla richiesta
 * @summary avvia un pagamento di un e-bollo (Modello e-bollo)
 */
export const postFrontRichiesteIdPagamentiIdPagamentoEbollo = (
  id: string,
  idPagamento: string,
  avviaEBolloRequest: AvviaEBolloRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<string>(
    {
      url: `/framework-cmp-domande/richieste/${id}/pagamenti/${idPagamento}/pagamento-ebollo`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: avviaEBolloRequest,
    },
    options,
  );
};

export const getPostFrontRichiesteIdPagamentiIdPagamentoEbolloMutationOptions =
  <TError = ErrorType<unknown>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<typeof postFrontRichiesteIdPagamentiIdPagamentoEbollo>
      >,
      TError,
      { id: string; idPagamento: string; data: AvviaEBolloRequest },
      TContext
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  }): UseMutationOptions<
    Awaited<ReturnType<typeof postFrontRichiesteIdPagamentiIdPagamentoEbollo>>,
    TError,
    { id: string; idPagamento: string; data: AvviaEBolloRequest },
    TContext
  > => {
    const { mutation: mutationOptions, request: requestOptions } =
      options ?? {};

    const mutationFn: MutationFunction<
      Awaited<
        ReturnType<typeof postFrontRichiesteIdPagamentiIdPagamentoEbollo>
      >,
      { id: string; idPagamento: string; data: AvviaEBolloRequest }
    > = props => {
      const { id, idPagamento, data } = props ?? {};

      return postFrontRichiesteIdPagamentiIdPagamentoEbollo(
        id,
        idPagamento,
        data,
        requestOptions,
      );
    };

    return { mutationFn, ...mutationOptions };
  };

export type PostFrontRichiesteIdPagamentiIdPagamentoEbolloMutationResult =
  NonNullable<
    Awaited<ReturnType<typeof postFrontRichiesteIdPagamentiIdPagamentoEbollo>>
  >;
export type PostFrontRichiesteIdPagamentiIdPagamentoEbolloMutationBody =
  AvviaEBolloRequest;
export type PostFrontRichiesteIdPagamentiIdPagamentoEbolloMutationError =
  ErrorType<unknown>;

/**
 * @summary avvia un pagamento di un e-bollo (Modello e-bollo)
 */
export const usePostFrontRichiesteIdPagamentiIdPagamentoEbollo = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontRichiesteIdPagamentiIdPagamentoEbollo>>,
    TError,
    { id: string; idPagamento: string; data: AvviaEBolloRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostFrontRichiesteIdPagamentiIdPagamentoEbolloMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Aggiorna lo stato della richiesta modulo
 * @summary Aggiorna lo stato della richiesta modulo
 */
export const postFrontRichiesteIdModuliIdStato = (
  id: string,
  idRichiestaModulo: string,
  cambiamentoStatoRequest: CambiamentoStatoRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    {
      url: `/framework-cmp-domande/richieste/${id}/moduli/${idRichiestaModulo}/stato`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: cambiamentoStatoRequest,
    },
    options,
  );
};

export const getPostFrontRichiesteIdModuliIdStatoMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontRichiesteIdModuliIdStato>>,
    TError,
    { id: string; idRichiestaModulo: string; data: CambiamentoStatoRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postFrontRichiesteIdModuliIdStato>>,
  TError,
  { id: string; idRichiestaModulo: string; data: CambiamentoStatoRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postFrontRichiesteIdModuliIdStato>>,
    { id: string; idRichiestaModulo: string; data: CambiamentoStatoRequest }
  > = props => {
    const { id, idRichiestaModulo, data } = props ?? {};

    return postFrontRichiesteIdModuliIdStato(
      id,
      idRichiestaModulo,
      data,
      requestOptions,
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type PostFrontRichiesteIdModuliIdStatoMutationResult = NonNullable<
  Awaited<ReturnType<typeof postFrontRichiesteIdModuliIdStato>>
>;
export type PostFrontRichiesteIdModuliIdStatoMutationBody =
  CambiamentoStatoRequest;
export type PostFrontRichiesteIdModuliIdStatoMutationError = ErrorType<unknown>;

/**
 * @summary Aggiorna lo stato della richiesta modulo
 */
export const usePostFrontRichiesteIdModuliIdStato = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontRichiesteIdModuliIdStato>>,
    TError,
    { id: string; idRichiestaModulo: string; data: CambiamentoStatoRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostFrontRichiesteIdModuliIdStatoMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * @summary Scarica un allegato
 */
export const getFrontRichiesteIdModuliIdSezioniIdFileCodice = (
  id: string,
  idRichiestaModulo: string,
  idSezione: string,
  codice: string,
  params?: GetFrontRichiesteIdModuliIdSezioniIdFileCodiceParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<Blob>(
    {
      url: `/framework-cmp-domande/richieste/${id}/moduli/${idRichiestaModulo}/sezioni/${idSezione}/file/${codice}`,
      method: 'get',
      params,
      responseType: 'blob',
      signal,
    },
    options,
  );
};

export const getGetFrontRichiesteIdModuliIdSezioniIdFileCodiceQueryKey = (
  id: string,
  idRichiestaModulo: string,
  idSezione: string,
  codice: string,
  params?: GetFrontRichiesteIdModuliIdSezioniIdFileCodiceParams,
) =>
  [
    `/framework-cmp-domande/richieste/${id}/moduli/${idRichiestaModulo}/sezioni/${idSezione}/file/${codice}`,
    ...(params ? [params] : []),
  ] as const;

export const getGetFrontRichiesteIdModuliIdSezioniIdFileCodiceQueryOptions = <
  TData = Awaited<
    ReturnType<typeof getFrontRichiesteIdModuliIdSezioniIdFileCodice>
  >,
  TError = ErrorType<unknown>,
>(
  id: string,
  idRichiestaModulo: string,
  idSezione: string,
  codice: string,
  params?: GetFrontRichiesteIdModuliIdSezioniIdFileCodiceParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<
        ReturnType<typeof getFrontRichiesteIdModuliIdSezioniIdFileCodice>
      >,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontRichiesteIdModuliIdSezioniIdFileCodice>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetFrontRichiesteIdModuliIdSezioniIdFileCodiceQueryKey(
      id,
      idRichiestaModulo,
      idSezione,
      codice,
      params,
    );

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontRichiesteIdModuliIdSezioniIdFileCodice>>
  > = ({ signal }) =>
    getFrontRichiesteIdModuliIdSezioniIdFileCodice(
      id,
      idRichiestaModulo,
      idSezione,
      codice,
      params,
      requestOptions,
      signal,
    );

  return {
    queryKey,
    queryFn,
    enabled: !!(id && idRichiestaModulo && idSezione && codice),
    ...queryOptions,
  };
};

export type GetFrontRichiesteIdModuliIdSezioniIdFileCodiceQueryResult =
  NonNullable<
    Awaited<ReturnType<typeof getFrontRichiesteIdModuliIdSezioniIdFileCodice>>
  >;
export type GetFrontRichiesteIdModuliIdSezioniIdFileCodiceQueryError =
  ErrorType<unknown>;

/**
 * @summary Scarica un allegato
 */
export const useGetFrontRichiesteIdModuliIdSezioniIdFileCodice = <
  TData = Awaited<
    ReturnType<typeof getFrontRichiesteIdModuliIdSezioniIdFileCodice>
  >,
  TError = ErrorType<unknown>,
>(
  id: string,
  idRichiestaModulo: string,
  idSezione: string,
  codice: string,
  params?: GetFrontRichiesteIdModuliIdSezioniIdFileCodiceParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<
        ReturnType<typeof getFrontRichiesteIdModuliIdSezioniIdFileCodice>
      >,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions =
    getGetFrontRichiesteIdModuliIdSezioniIdFileCodiceQueryOptions(
      id,
      idRichiestaModulo,
      idSezione,
      codice,
      params,
      options,
    );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Aggiunge allegato
 * @summary Aggiunge allegato
 */
export const postFrontRichiesteIdModuliIdSezioniIdFileCodice = (
  id: string,
  idRichiestaModulo: string,
  idSezione: string,
  codice: string,
  postFrontRichiesteIdModuliIdSezioniIdFileCodiceBody: PostFrontRichiesteIdModuliIdSezioniIdFileCodiceBody,
  params?: PostFrontRichiesteIdModuliIdSezioniIdFileCodiceParams,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  const formData = new FormData();
  formData.append(
    'file',
    postFrontRichiesteIdModuliIdSezioniIdFileCodiceBody.file,
  );

  return apiClientRequest<AggiungiAllegatoResult>(
    {
      url: `/framework-cmp-domande/richieste/${id}/moduli/${idRichiestaModulo}/sezioni/${idSezione}/file/${codice}`,
      method: 'post',
      headers: { 'Content-Type': 'multipart/form-data' },
      data: formData,
      params,
    },
    options,
  );
};

export const getPostFrontRichiesteIdModuliIdSezioniIdFileCodiceMutationOptions =
  <TError = ErrorType<unknown>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<typeof postFrontRichiesteIdModuliIdSezioniIdFileCodice>
      >,
      TError,
      {
        id: string;
        idRichiestaModulo: string;
        idSezione: string;
        codice: string;
        data: PostFrontRichiesteIdModuliIdSezioniIdFileCodiceBody;
        params?: PostFrontRichiesteIdModuliIdSezioniIdFileCodiceParams;
      },
      TContext
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  }): UseMutationOptions<
    Awaited<ReturnType<typeof postFrontRichiesteIdModuliIdSezioniIdFileCodice>>,
    TError,
    {
      id: string;
      idRichiestaModulo: string;
      idSezione: string;
      codice: string;
      data: PostFrontRichiesteIdModuliIdSezioniIdFileCodiceBody;
      params?: PostFrontRichiesteIdModuliIdSezioniIdFileCodiceParams;
    },
    TContext
  > => {
    const { mutation: mutationOptions, request: requestOptions } =
      options ?? {};

    const mutationFn: MutationFunction<
      Awaited<
        ReturnType<typeof postFrontRichiesteIdModuliIdSezioniIdFileCodice>
      >,
      {
        id: string;
        idRichiestaModulo: string;
        idSezione: string;
        codice: string;
        data: PostFrontRichiesteIdModuliIdSezioniIdFileCodiceBody;
        params?: PostFrontRichiesteIdModuliIdSezioniIdFileCodiceParams;
      }
    > = props => {
      const { id, idRichiestaModulo, idSezione, codice, data, params } =
        props ?? {};

      return postFrontRichiesteIdModuliIdSezioniIdFileCodice(
        id,
        idRichiestaModulo,
        idSezione,
        codice,
        data,
        params,
        requestOptions,
      );
    };

    return { mutationFn, ...mutationOptions };
  };

export type PostFrontRichiesteIdModuliIdSezioniIdFileCodiceMutationResult =
  NonNullable<
    Awaited<ReturnType<typeof postFrontRichiesteIdModuliIdSezioniIdFileCodice>>
  >;
export type PostFrontRichiesteIdModuliIdSezioniIdFileCodiceMutationBody =
  PostFrontRichiesteIdModuliIdSezioniIdFileCodiceBody;
export type PostFrontRichiesteIdModuliIdSezioniIdFileCodiceMutationError =
  ErrorType<unknown>;

/**
 * @summary Aggiunge allegato
 */
export const usePostFrontRichiesteIdModuliIdSezioniIdFileCodice = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontRichiesteIdModuliIdSezioniIdFileCodice>>,
    TError,
    {
      id: string;
      idRichiestaModulo: string;
      idSezione: string;
      codice: string;
      data: PostFrontRichiesteIdModuliIdSezioniIdFileCodiceBody;
      params?: PostFrontRichiesteIdModuliIdSezioniIdFileCodiceParams;
    },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostFrontRichiesteIdModuliIdSezioniIdFileCodiceMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * @summary Cancella un allegato
 */
export const deleteFrontRichiesteIdModuliIdSezioniIdFileCodice = (
  id: string,
  idRichiestaModulo: string,
  idSezione: string,
  codice: string,
  params?: DeleteFrontRichiesteIdModuliIdSezioniIdFileCodiceParams,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    {
      url: `/framework-cmp-domande/richieste/${id}/moduli/${idRichiestaModulo}/sezioni/${idSezione}/file/${codice}`,
      method: 'delete',
      params,
    },
    options,
  );
};

export const getDeleteFrontRichiesteIdModuliIdSezioniIdFileCodiceMutationOptions =
  <TError = ErrorType<unknown>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<typeof deleteFrontRichiesteIdModuliIdSezioniIdFileCodice>
      >,
      TError,
      {
        id: string;
        idRichiestaModulo: string;
        idSezione: string;
        codice: string;
        params?: DeleteFrontRichiesteIdModuliIdSezioniIdFileCodiceParams;
      },
      TContext
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  }): UseMutationOptions<
    Awaited<
      ReturnType<typeof deleteFrontRichiesteIdModuliIdSezioniIdFileCodice>
    >,
    TError,
    {
      id: string;
      idRichiestaModulo: string;
      idSezione: string;
      codice: string;
      params?: DeleteFrontRichiesteIdModuliIdSezioniIdFileCodiceParams;
    },
    TContext
  > => {
    const { mutation: mutationOptions, request: requestOptions } =
      options ?? {};

    const mutationFn: MutationFunction<
      Awaited<
        ReturnType<typeof deleteFrontRichiesteIdModuliIdSezioniIdFileCodice>
      >,
      {
        id: string;
        idRichiestaModulo: string;
        idSezione: string;
        codice: string;
        params?: DeleteFrontRichiesteIdModuliIdSezioniIdFileCodiceParams;
      }
    > = props => {
      const { id, idRichiestaModulo, idSezione, codice, params } = props ?? {};

      return deleteFrontRichiesteIdModuliIdSezioniIdFileCodice(
        id,
        idRichiestaModulo,
        idSezione,
        codice,
        params,
        requestOptions,
      );
    };

    return { mutationFn, ...mutationOptions };
  };

export type DeleteFrontRichiesteIdModuliIdSezioniIdFileCodiceMutationResult =
  NonNullable<
    Awaited<
      ReturnType<typeof deleteFrontRichiesteIdModuliIdSezioniIdFileCodice>
    >
  >;

export type DeleteFrontRichiesteIdModuliIdSezioniIdFileCodiceMutationError =
  ErrorType<unknown>;

/**
 * @summary Cancella un allegato
 */
export const useDeleteFrontRichiesteIdModuliIdSezioniIdFileCodice = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<
      ReturnType<typeof deleteFrontRichiesteIdModuliIdSezioniIdFileCodice>
    >,
    TError,
    {
      id: string;
      idRichiestaModulo: string;
      idSezione: string;
      codice: string;
      params?: DeleteFrontRichiesteIdModuliIdSezioniIdFileCodiceParams;
    },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getDeleteFrontRichiesteIdModuliIdSezioniIdFileCodiceMutationOptions(
      options,
    );

  return useMutation(mutationOptions);
};
/**
 * Recupera tutte le deleghe presenti sui moduli
 * @summary Recupera tutte le deleghe presenti sui moduli
 */
export const getFrontRichiesteIdModuliDeleghe = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<DelegaModulo[]>(
    {
      url: `/framework-cmp-domande/richieste/${id}/moduli/deleghe`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetFrontRichiesteIdModuliDelegheQueryKey = (id: string) =>
  [`/framework-cmp-domande/richieste/${id}/moduli/deleghe`] as const;

export const getGetFrontRichiesteIdModuliDelegheQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteIdModuliDeleghe>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteIdModuliDeleghe>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontRichiesteIdModuliDeleghe>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetFrontRichiesteIdModuliDelegheQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontRichiesteIdModuliDeleghe>>
  > = ({ signal }) =>
    getFrontRichiesteIdModuliDeleghe(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetFrontRichiesteIdModuliDelegheQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontRichiesteIdModuliDeleghe>>
>;
export type GetFrontRichiesteIdModuliDelegheQueryError = ErrorType<unknown>;

/**
 * @summary Recupera tutte le deleghe presenti sui moduli
 */
export const useGetFrontRichiesteIdModuliDeleghe = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteIdModuliDeleghe>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteIdModuliDeleghe>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontRichiesteIdModuliDelegheQueryOptions(
    id,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Aggiorna le deleghe di tutte le richieste modulo
 * @summary Aggiorna le deleghe
 */
export const postFrontRichiesteIdModuliDeleghe = (
  id: string,
  updateDelegaModulo: UpdateDelegaModulo[],
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<DelegaModulo[]>(
    {
      url: `/framework-cmp-domande/richieste/${id}/moduli/deleghe`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: updateDelegaModulo,
    },
    options,
  );
};

export const getPostFrontRichiesteIdModuliDelegheMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontRichiesteIdModuliDeleghe>>,
    TError,
    { id: string; data: UpdateDelegaModulo[] },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postFrontRichiesteIdModuliDeleghe>>,
  TError,
  { id: string; data: UpdateDelegaModulo[] },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postFrontRichiesteIdModuliDeleghe>>,
    { id: string; data: UpdateDelegaModulo[] }
  > = props => {
    const { id, data } = props ?? {};

    return postFrontRichiesteIdModuliDeleghe(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostFrontRichiesteIdModuliDelegheMutationResult = NonNullable<
  Awaited<ReturnType<typeof postFrontRichiesteIdModuliDeleghe>>
>;
export type PostFrontRichiesteIdModuliDelegheMutationBody =
  UpdateDelegaModulo[];
export type PostFrontRichiesteIdModuliDelegheMutationError = ErrorType<unknown>;

/**
 * @summary Aggiorna le deleghe
 */
export const usePostFrontRichiesteIdModuliDeleghe = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontRichiesteIdModuliDeleghe>>,
    TError,
    { id: string; data: UpdateDelegaModulo[] },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostFrontRichiesteIdModuliDelegheMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Informazioni sul gestore della richiesta
 * @summary Informazioni sul gestore
 */
export const getFrontRichiesteIdGestore = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<InfoGestore>(
    {
      url: `/framework-cmp-domande/richieste/${id}/gestore`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetFrontRichiesteIdGestoreQueryKey = (id: string) =>
  [`/framework-cmp-domande/richieste/${id}/gestore`] as const;

export const getGetFrontRichiesteIdGestoreQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteIdGestore>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteIdGestore>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontRichiesteIdGestore>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetFrontRichiesteIdGestoreQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontRichiesteIdGestore>>
  > = ({ signal }) => getFrontRichiesteIdGestore(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetFrontRichiesteIdGestoreQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontRichiesteIdGestore>>
>;
export type GetFrontRichiesteIdGestoreQueryError = ErrorType<unknown>;

/**
 * @summary Informazioni sul gestore
 */
export const useGetFrontRichiesteIdGestore = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteIdGestore>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteIdGestore>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontRichiesteIdGestoreQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Aggiorna il gestore della richiesta
 * @summary Aggiorna il gestore
 */
export const postFrontRichiesteIdGestore = (
  id: string,
  updateUtenteRequest: UpdateUtenteRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<InfoGestore>(
    {
      url: `/framework-cmp-domande/richieste/${id}/gestore`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: updateUtenteRequest,
    },
    options,
  );
};

export const getPostFrontRichiesteIdGestoreMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontRichiesteIdGestore>>,
    TError,
    { id: string; data: UpdateUtenteRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postFrontRichiesteIdGestore>>,
  TError,
  { id: string; data: UpdateUtenteRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postFrontRichiesteIdGestore>>,
    { id: string; data: UpdateUtenteRequest }
  > = props => {
    const { id, data } = props ?? {};

    return postFrontRichiesteIdGestore(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostFrontRichiesteIdGestoreMutationResult = NonNullable<
  Awaited<ReturnType<typeof postFrontRichiesteIdGestore>>
>;
export type PostFrontRichiesteIdGestoreMutationBody = UpdateUtenteRequest;
export type PostFrontRichiesteIdGestoreMutationError = ErrorType<unknown>;

/**
 * @summary Aggiorna il gestore
 */
export const usePostFrontRichiesteIdGestore = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontRichiesteIdGestore>>,
    TError,
    { id: string; data: UpdateUtenteRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostFrontRichiesteIdGestoreMutationOptions(options);

  return useMutation(mutationOptions);
};
export const postFrontRichiesteIdDelega = (
  id: string,
  postFrontRichiesteIdDelegaBody: PostFrontRichiesteIdDelegaBody,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  const formData = new FormData();
  if (postFrontRichiesteIdDelegaBody.file !== undefined) {
    formData.append('file', postFrontRichiesteIdDelegaBody.file);
  }
  formData.append('datiDelega', postFrontRichiesteIdDelegaBody.datiDelega);

  return apiClientRequest<void>(
    {
      url: `/framework-cmp-domande/richieste/${id}/delega`,
      method: 'post',
      headers: { 'Content-Type': 'multipart/form-data' },
      data: formData,
    },
    options,
  );
};

export const getPostFrontRichiesteIdDelegaMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontRichiesteIdDelega>>,
    TError,
    { id: string; data: PostFrontRichiesteIdDelegaBody },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postFrontRichiesteIdDelega>>,
  TError,
  { id: string; data: PostFrontRichiesteIdDelegaBody },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postFrontRichiesteIdDelega>>,
    { id: string; data: PostFrontRichiesteIdDelegaBody }
  > = props => {
    const { id, data } = props ?? {};

    return postFrontRichiesteIdDelega(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostFrontRichiesteIdDelegaMutationResult = NonNullable<
  Awaited<ReturnType<typeof postFrontRichiesteIdDelega>>
>;
export type PostFrontRichiesteIdDelegaMutationBody =
  PostFrontRichiesteIdDelegaBody;
export type PostFrontRichiesteIdDelegaMutationError = ErrorType<unknown>;

export const usePostFrontRichiesteIdDelega = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontRichiesteIdDelega>>,
    TError,
    { id: string; data: PostFrontRichiesteIdDelegaBody },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostFrontRichiesteIdDelegaMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * @summary Carica una delega
 */
export const deleteFrontRichiesteIdDelega = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    { url: `/framework-cmp-domande/richieste/${id}/delega`, method: 'delete' },
    options,
  );
};

export const getDeleteFrontRichiesteIdDelegaMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteFrontRichiesteIdDelega>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteFrontRichiesteIdDelega>>,
  TError,
  { id: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteFrontRichiesteIdDelega>>,
    { id: string }
  > = props => {
    const { id } = props ?? {};

    return deleteFrontRichiesteIdDelega(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteFrontRichiesteIdDelegaMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteFrontRichiesteIdDelega>>
>;

export type DeleteFrontRichiesteIdDelegaMutationError = ErrorType<unknown>;

/**
 * @summary Carica una delega
 */
export const useDeleteFrontRichiesteIdDelega = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteFrontRichiesteIdDelega>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getDeleteFrontRichiesteIdDelegaMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Duplica la richiesta
 * @summary Duplicazione della richiesta
 */
export const postFrontRichiesteIdCopia = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<string>(
    { url: `/framework-cmp-domande/richieste/${id}/copia`, method: 'post' },
    options,
  );
};

export const getPostFrontRichiesteIdCopiaMutationOptions = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontRichiesteIdCopia>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postFrontRichiesteIdCopia>>,
  TError,
  { id: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postFrontRichiesteIdCopia>>,
    { id: string }
  > = props => {
    const { id } = props ?? {};

    return postFrontRichiesteIdCopia(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostFrontRichiesteIdCopiaMutationResult = NonNullable<
  Awaited<ReturnType<typeof postFrontRichiesteIdCopia>>
>;

export type PostFrontRichiesteIdCopiaMutationError = ErrorType<WsProblema>;

/**
 * @summary Duplicazione della richiesta
 */
export const usePostFrontRichiesteIdCopia = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontRichiesteIdCopia>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostFrontRichiesteIdCopiaMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Ricerca istanze di comunicazione di una richiesta per il richiedente. E' ordinabile per idTipoComunicazione
 * @summary Ricerca istanze di comunicazione di una richiesta per il richiedente
 */
export const getFrontRichiesteIdComunicazioni = (
  id: string,
  params?: GetFrontRichiesteIdComunicazioniParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<WsRispostaPaginataListComunicazioneDTO>(
    {
      url: `/framework-cmp-domande/richieste/${id}/comunicazioni`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetFrontRichiesteIdComunicazioniQueryKey = (
  id: string,
  params?: GetFrontRichiesteIdComunicazioniParams,
) =>
  [
    `/framework-cmp-domande/richieste/${id}/comunicazioni`,
    ...(params ? [params] : []),
  ] as const;

export const getGetFrontRichiesteIdComunicazioniQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteIdComunicazioni>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  params?: GetFrontRichiesteIdComunicazioniParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteIdComunicazioni>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontRichiesteIdComunicazioni>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetFrontRichiesteIdComunicazioniQueryKey(id, params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontRichiesteIdComunicazioni>>
  > = ({ signal }) =>
    getFrontRichiesteIdComunicazioni(id, params, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetFrontRichiesteIdComunicazioniQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontRichiesteIdComunicazioni>>
>;
export type GetFrontRichiesteIdComunicazioniQueryError = ErrorType<unknown>;

/**
 * @summary Ricerca istanze di comunicazione di una richiesta per il richiedente
 */
export const useGetFrontRichiesteIdComunicazioni = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteIdComunicazioni>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  params?: GetFrontRichiesteIdComunicazioniParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteIdComunicazioni>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontRichiesteIdComunicazioniQueryOptions(
    id,
    params,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Crea una Comunicazione
 * @summary Crea una Comunicazione
 */
export const postFrontRichiesteIdComunicazioni = (
  id: string,
  comunicazioneFrontOfficeCreateRequest: ComunicazioneFrontOfficeCreateRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<ComunicazioneDTO>(
    {
      url: `/framework-cmp-domande/richieste/${id}/comunicazioni`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: comunicazioneFrontOfficeCreateRequest,
    },
    options,
  );
};

export const getPostFrontRichiesteIdComunicazioniMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontRichiesteIdComunicazioni>>,
    TError,
    { id: string; data: ComunicazioneFrontOfficeCreateRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postFrontRichiesteIdComunicazioni>>,
  TError,
  { id: string; data: ComunicazioneFrontOfficeCreateRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postFrontRichiesteIdComunicazioni>>,
    { id: string; data: ComunicazioneFrontOfficeCreateRequest }
  > = props => {
    const { id, data } = props ?? {};

    return postFrontRichiesteIdComunicazioni(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostFrontRichiesteIdComunicazioniMutationResult = NonNullable<
  Awaited<ReturnType<typeof postFrontRichiesteIdComunicazioni>>
>;
export type PostFrontRichiesteIdComunicazioniMutationBody =
  ComunicazioneFrontOfficeCreateRequest;
export type PostFrontRichiesteIdComunicazioniMutationError = ErrorType<unknown>;

/**
 * @summary Crea una Comunicazione
 */
export const usePostFrontRichiesteIdComunicazioni = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontRichiesteIdComunicazioni>>,
    TError,
    { id: string; data: ComunicazioneFrontOfficeCreateRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostFrontRichiesteIdComunicazioniMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Crea una risposta a una comunicazione esistente, ereditando il tipo comunicazione e la modalità di trasmissione dall'originale
 * @summary Rispondi a una Comunicazione
 */
export const postFrontRichiesteIdComunicazioniCreaRisposta = (
  id: string,
  rispondiComunicazioneRequest: RispondiComunicazioneRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<ComunicazioneDTO>(
    {
      url: `/framework-cmp-domande/richieste/${id}/comunicazioni/crea-risposta`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: rispondiComunicazioneRequest,
    },
    options,
  );
};

export const getPostFrontRichiesteIdComunicazioniCreaRispostaMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontRichiesteIdComunicazioniCreaRisposta>>,
    TError,
    { id: string; data: RispondiComunicazioneRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postFrontRichiesteIdComunicazioniCreaRisposta>>,
  TError,
  { id: string; data: RispondiComunicazioneRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postFrontRichiesteIdComunicazioniCreaRisposta>>,
    { id: string; data: RispondiComunicazioneRequest }
  > = props => {
    const { id, data } = props ?? {};

    return postFrontRichiesteIdComunicazioniCreaRisposta(
      id,
      data,
      requestOptions,
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type PostFrontRichiesteIdComunicazioniCreaRispostaMutationResult =
  NonNullable<
    Awaited<ReturnType<typeof postFrontRichiesteIdComunicazioniCreaRisposta>>
  >;
export type PostFrontRichiesteIdComunicazioniCreaRispostaMutationBody =
  RispondiComunicazioneRequest;
export type PostFrontRichiesteIdComunicazioniCreaRispostaMutationError =
  ErrorType<unknown>;

/**
 * @summary Rispondi a una Comunicazione
 */
export const usePostFrontRichiesteIdComunicazioniCreaRisposta = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontRichiesteIdComunicazioniCreaRisposta>>,
    TError,
    { id: string; data: RispondiComunicazioneRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostFrontRichiesteIdComunicazioniCreaRispostaMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Crea una anteprima della comunicazione con popolati oggetto e messaggio
 * @summary Crea una anteprima della comunicazione con popolati oggetto e messaggio
 */
export const postFrontRichiesteIdComunicazioniAnteprima = (
  id: string,
  comunicazioneFrontOfficeCreateRequest: ComunicazioneFrontOfficeCreateRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<ComunicazioneAnteprimaDTO>(
    {
      url: `/framework-cmp-domande/richieste/${id}/comunicazioni/anteprima`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: comunicazioneFrontOfficeCreateRequest,
    },
    options,
  );
};

export const getPostFrontRichiesteIdComunicazioniAnteprimaMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontRichiesteIdComunicazioniAnteprima>>,
    TError,
    { id: string; data: ComunicazioneFrontOfficeCreateRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postFrontRichiesteIdComunicazioniAnteprima>>,
  TError,
  { id: string; data: ComunicazioneFrontOfficeCreateRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postFrontRichiesteIdComunicazioniAnteprima>>,
    { id: string; data: ComunicazioneFrontOfficeCreateRequest }
  > = props => {
    const { id, data } = props ?? {};

    return postFrontRichiesteIdComunicazioniAnteprima(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostFrontRichiesteIdComunicazioniAnteprimaMutationResult =
  NonNullable<
    Awaited<ReturnType<typeof postFrontRichiesteIdComunicazioniAnteprima>>
  >;
export type PostFrontRichiesteIdComunicazioniAnteprimaMutationBody =
  ComunicazioneFrontOfficeCreateRequest;
export type PostFrontRichiesteIdComunicazioniAnteprimaMutationError =
  ErrorType<unknown>;

/**
 * @summary Crea una anteprima della comunicazione con popolati oggetto e messaggio
 */
export const usePostFrontRichiesteIdComunicazioniAnteprima = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontRichiesteIdComunicazioniAnteprima>>,
    TError,
    { id: string; data: ComunicazioneFrontOfficeCreateRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostFrontRichiesteIdComunicazioniAnteprimaMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Restituisce la lista degli id delle competenze associate a una richiesta
 * @summary Chiamata b2b dal be
 */
export const postFrontRichiesteIdCompetenzeRichiesta = (
  id: string,
  utente: Utente,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<string[]>(
    {
      url: `/framework-cmp-domande/richieste/${id}/competenze-richiesta`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: utente,
    },
    options,
  );
};

export const getPostFrontRichiesteIdCompetenzeRichiestaMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontRichiesteIdCompetenzeRichiesta>>,
    TError,
    { id: string; data: Utente },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postFrontRichiesteIdCompetenzeRichiesta>>,
  TError,
  { id: string; data: Utente },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postFrontRichiesteIdCompetenzeRichiesta>>,
    { id: string; data: Utente }
  > = props => {
    const { id, data } = props ?? {};

    return postFrontRichiesteIdCompetenzeRichiesta(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostFrontRichiesteIdCompetenzeRichiestaMutationResult = NonNullable<
  Awaited<ReturnType<typeof postFrontRichiesteIdCompetenzeRichiesta>>
>;
export type PostFrontRichiesteIdCompetenzeRichiestaMutationBody = Utente;
export type PostFrontRichiesteIdCompetenzeRichiestaMutationError =
  ErrorType<unknown>;

/**
 * @summary Chiamata b2b dal be
 */
export const usePostFrontRichiesteIdCompetenzeRichiesta = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontRichiesteIdCompetenzeRichiesta>>,
    TError,
    { id: string; data: Utente },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostFrontRichiesteIdCompetenzeRichiestaMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Invio di una richiesta
 * @summary Invio di una richiesta
 */
export const postFrontRichiesteIdAzioniInvia = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<RichiestaDTO>(
    {
      url: `/framework-cmp-domande/richieste/${id}/azioni/invia`,
      method: 'post',
    },
    options,
  );
};

export const getPostFrontRichiesteIdAzioniInviaMutationOptions = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontRichiesteIdAzioniInvia>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postFrontRichiesteIdAzioniInvia>>,
  TError,
  { id: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postFrontRichiesteIdAzioniInvia>>,
    { id: string }
  > = props => {
    const { id } = props ?? {};

    return postFrontRichiesteIdAzioniInvia(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostFrontRichiesteIdAzioniInviaMutationResult = NonNullable<
  Awaited<ReturnType<typeof postFrontRichiesteIdAzioniInvia>>
>;

export type PostFrontRichiesteIdAzioniInviaMutationError =
  ErrorType<WsProblema>;

/**
 * @summary Invio di una richiesta
 */
export const usePostFrontRichiesteIdAzioniInvia = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontRichiesteIdAzioniInvia>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostFrontRichiesteIdAzioniInviaMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Processo che integra una richiesta
 * @summary Integrazione di una richiesta
 */
export const postFrontRichiesteIdAzioniIntegra = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<RichiestaDTO>(
    {
      url: `/framework-cmp-domande/richieste/${id}/azioni/integra`,
      method: 'post',
    },
    options,
  );
};

export const getPostFrontRichiesteIdAzioniIntegraMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontRichiesteIdAzioniIntegra>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postFrontRichiesteIdAzioniIntegra>>,
  TError,
  { id: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postFrontRichiesteIdAzioniIntegra>>,
    { id: string }
  > = props => {
    const { id } = props ?? {};

    return postFrontRichiesteIdAzioniIntegra(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostFrontRichiesteIdAzioniIntegraMutationResult = NonNullable<
  Awaited<ReturnType<typeof postFrontRichiesteIdAzioniIntegra>>
>;

export type PostFrontRichiesteIdAzioniIntegraMutationError = ErrorType<unknown>;

/**
 * @summary Integrazione di una richiesta
 */
export const usePostFrontRichiesteIdAzioniIntegra = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontRichiesteIdAzioniIntegra>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostFrontRichiesteIdAzioniIntegraMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Caricamento documento firmato
 * @summary Caricamento documento firmato
 */
export const postFrontRichiesteIdModuloIdDocumentoFirma = (
  idRichiesta: string,
  idRichiestaModulo: string,
  postFrontRichiesteIdModuloIdDocumentoFirmaBody:
    | PostFrontRichiesteIdModuloIdDocumentoFirmaBodyOne
    | PostFrontRichiesteIdModuloIdDocumentoFirmaBodyTwo,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<RichiestaModuloDTO>(
    {
      url: `/framework-cmp-domande/richieste/${idRichiesta}/modulo/${idRichiestaModulo}/documento-firma`,
      method: 'post',
      data: postFrontRichiesteIdModuloIdDocumentoFirmaBody,
    },
    options,
  );
};

export const getPostFrontRichiesteIdModuloIdDocumentoFirmaMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontRichiesteIdModuloIdDocumentoFirma>>,
    TError,
    {
      idRichiesta: string;
      idRichiestaModulo: string;
      data:
        | PostFrontRichiesteIdModuloIdDocumentoFirmaBodyOne
        | PostFrontRichiesteIdModuloIdDocumentoFirmaBodyTwo;
    },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postFrontRichiesteIdModuloIdDocumentoFirma>>,
  TError,
  {
    idRichiesta: string;
    idRichiestaModulo: string;
    data:
      | PostFrontRichiesteIdModuloIdDocumentoFirmaBodyOne
      | PostFrontRichiesteIdModuloIdDocumentoFirmaBodyTwo;
  },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postFrontRichiesteIdModuloIdDocumentoFirma>>,
    {
      idRichiesta: string;
      idRichiestaModulo: string;
      data:
        | PostFrontRichiesteIdModuloIdDocumentoFirmaBodyOne
        | PostFrontRichiesteIdModuloIdDocumentoFirmaBodyTwo;
    }
  > = props => {
    const { idRichiesta, idRichiestaModulo, data } = props ?? {};

    return postFrontRichiesteIdModuloIdDocumentoFirma(
      idRichiesta,
      idRichiestaModulo,
      data,
      requestOptions,
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type PostFrontRichiesteIdModuloIdDocumentoFirmaMutationResult =
  NonNullable<
    Awaited<ReturnType<typeof postFrontRichiesteIdModuloIdDocumentoFirma>>
  >;
export type PostFrontRichiesteIdModuloIdDocumentoFirmaMutationBody =
  | PostFrontRichiesteIdModuloIdDocumentoFirmaBodyOne
  | PostFrontRichiesteIdModuloIdDocumentoFirmaBodyTwo;
export type PostFrontRichiesteIdModuloIdDocumentoFirmaMutationError =
  ErrorType<unknown>;

/**
 * @summary Caricamento documento firmato
 */
export const usePostFrontRichiesteIdModuloIdDocumentoFirma = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontRichiesteIdModuloIdDocumentoFirma>>,
    TError,
    {
      idRichiesta: string;
      idRichiestaModulo: string;
      data:
        | PostFrontRichiesteIdModuloIdDocumentoFirmaBodyOne
        | PostFrontRichiesteIdModuloIdDocumentoFirmaBodyTwo;
    },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostFrontRichiesteIdModuloIdDocumentoFirmaMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Chiamata batch che uno scheduler può effettuare per verificare lo stato di pagamenti modello3
 * @summary Chiamata batch che uno scheduler può effettuare per verificare lo stato di pagamenti modello3
 */
export const postFrontRichiesteVerificaPagamentiModello3 = (
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<SchedulationResult>(
    {
      url: `/framework-cmp-domande/richieste/verifica-pagamenti-modello3`,
      method: 'post',
    },
    options,
  );
};

export const getPostFrontRichiesteVerificaPagamentiModello3MutationOptions = <
  TError = ErrorType<unknown>,
  TVariables = void,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontRichiesteVerificaPagamentiModello3>>,
    TError,
    TVariables,
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postFrontRichiesteVerificaPagamentiModello3>>,
  TError,
  TVariables,
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postFrontRichiesteVerificaPagamentiModello3>>,
    TVariables
  > = () => {
    return postFrontRichiesteVerificaPagamentiModello3(requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostFrontRichiesteVerificaPagamentiModello3MutationResult =
  NonNullable<
    Awaited<ReturnType<typeof postFrontRichiesteVerificaPagamentiModello3>>
  >;

export type PostFrontRichiesteVerificaPagamentiModello3MutationError =
  ErrorType<unknown>;

/**
 * @summary Chiamata batch che uno scheduler può effettuare per verificare lo stato di pagamenti modello3
 */
export const usePostFrontRichiesteVerificaPagamentiModello3 = <
  TError = ErrorType<unknown>,
  TVariables = void,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontRichiesteVerificaPagamentiModello3>>,
    TError,
    TVariables,
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostFrontRichiesteVerificaPagamentiModello3MutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Avvia sincronizzazione subentro su una richiesta di un procedimento pratica
 * @summary Avvia sincronizzazione subentro richiesta di una pratica
 */
export const postFrontRichiestePraticaIdSubentro = (
  id: string,
  subentroDTO: SubentroDTO,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<string>(
    {
      url: `/framework-cmp-domande/richieste/pratica/${id}/subentro`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: subentroDTO,
    },
    options,
  );
};

export const getPostFrontRichiestePraticaIdSubentroMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontRichiestePraticaIdSubentro>>,
    TError,
    { id: string; data: SubentroDTO },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postFrontRichiestePraticaIdSubentro>>,
  TError,
  { id: string; data: SubentroDTO },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postFrontRichiestePraticaIdSubentro>>,
    { id: string; data: SubentroDTO }
  > = props => {
    const { id, data } = props ?? {};

    return postFrontRichiestePraticaIdSubentro(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostFrontRichiestePraticaIdSubentroMutationResult = NonNullable<
  Awaited<ReturnType<typeof postFrontRichiestePraticaIdSubentro>>
>;
export type PostFrontRichiestePraticaIdSubentroMutationBody = SubentroDTO;
export type PostFrontRichiestePraticaIdSubentroMutationError =
  ErrorType<unknown>;

/**
 * @summary Avvia sincronizzazione subentro richiesta di una pratica
 */
export const usePostFrontRichiestePraticaIdSubentro = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontRichiestePraticaIdSubentro>>,
    TError,
    { id: string; data: SubentroDTO },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostFrontRichiestePraticaIdSubentroMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Aggiunge l'id istanza procedimento di una pratica, alla richiesta
 * @summary Modifica richiesta
 */
export const patchFrontRichiesteIdLinkPraticaId = (
  id: string,
  idIstanzaProcedimentoPratica: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<RichiestaDTO>(
    {
      url: `/framework-cmp-domande/richieste/${id}/link-pratica/${idIstanzaProcedimentoPratica}`,
      method: 'patch',
    },
    options,
  );
};

export const getPatchFrontRichiesteIdLinkPraticaIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof patchFrontRichiesteIdLinkPraticaId>>,
    TError,
    { id: string; idIstanzaProcedimentoPratica: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof patchFrontRichiesteIdLinkPraticaId>>,
  TError,
  { id: string; idIstanzaProcedimentoPratica: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof patchFrontRichiesteIdLinkPraticaId>>,
    { id: string; idIstanzaProcedimentoPratica: string }
  > = props => {
    const { id, idIstanzaProcedimentoPratica } = props ?? {};

    return patchFrontRichiesteIdLinkPraticaId(
      id,
      idIstanzaProcedimentoPratica,
      requestOptions,
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type PatchFrontRichiesteIdLinkPraticaIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof patchFrontRichiesteIdLinkPraticaId>>
>;

export type PatchFrontRichiesteIdLinkPraticaIdMutationError =
  ErrorType<unknown>;

/**
 * @summary Modifica richiesta
 */
export const usePatchFrontRichiesteIdLinkPraticaId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof patchFrontRichiesteIdLinkPraticaId>>,
    TError,
    { id: string; idIstanzaProcedimentoPratica: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPatchFrontRichiesteIdLinkPraticaIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Ottiene l'elenco delle richieste effettuate dall'utente
 * @summary Elenco richieste
 */
export const getFrontRichieste = (
  params?: GetFrontRichiesteParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<WsRispostaPaginataListRichiestaDTO>(
    { url: `/framework-cmp-domande/richieste`, method: 'get', params, signal },
    options,
  );
};

export const getGetFrontRichiesteQueryKey = (
  params?: GetFrontRichiesteParams,
) => [`/framework-cmp-domande/richieste`, ...(params ? [params] : [])] as const;

export const getGetFrontRichiesteQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontRichieste>>,
  TError = ErrorType<unknown>,
>(
  params?: GetFrontRichiesteParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichieste>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontRichieste>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetFrontRichiesteQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontRichieste>>
  > = ({ signal }) => getFrontRichieste(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetFrontRichiesteQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontRichieste>>
>;
export type GetFrontRichiesteQueryError = ErrorType<unknown>;

/**
 * @summary Elenco richieste
 */
export const useGetFrontRichieste = <
  TData = Awaited<ReturnType<typeof getFrontRichieste>>,
  TError = ErrorType<unknown>,
>(
  params?: GetFrontRichiesteParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichieste>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontRichiesteQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Ricerca tipi comunicazione di una richiesta per il richiedente. Ordinabile per codice tipo comunicazione se a true il campo ordinaPerCodice in requestParam
 * @summary Ricerca tipi comunicazione di una richiesta per il richiedente
 */
export const getFrontRichiesteIdTipiComunicazione = (
  id: string,
  params?: GetFrontRichiesteIdTipiComunicazioneParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<TipoComunicazioneDTO[]>(
    {
      url: `/framework-cmp-domande/richieste/${id}/tipi-comunicazione`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetFrontRichiesteIdTipiComunicazioneQueryKey = (
  id: string,
  params?: GetFrontRichiesteIdTipiComunicazioneParams,
) =>
  [
    `/framework-cmp-domande/richieste/${id}/tipi-comunicazione`,
    ...(params ? [params] : []),
  ] as const;

export const getGetFrontRichiesteIdTipiComunicazioneQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteIdTipiComunicazione>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  params?: GetFrontRichiesteIdTipiComunicazioneParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteIdTipiComunicazione>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontRichiesteIdTipiComunicazione>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetFrontRichiesteIdTipiComunicazioneQueryKey(id, params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontRichiesteIdTipiComunicazione>>
  > = ({ signal }) =>
    getFrontRichiesteIdTipiComunicazione(id, params, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetFrontRichiesteIdTipiComunicazioneQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontRichiesteIdTipiComunicazione>>
>;
export type GetFrontRichiesteIdTipiComunicazioneQueryError = ErrorType<unknown>;

/**
 * @summary Ricerca tipi comunicazione di una richiesta per il richiedente
 */
export const useGetFrontRichiesteIdTipiComunicazione = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteIdTipiComunicazione>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  params?: GetFrontRichiesteIdTipiComunicazioneParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteIdTipiComunicazione>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontRichiesteIdTipiComunicazioneQueryOptions(
    id,
    params,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Restituisce il richiedente di una richiesta
 * @summary Recupero richiedente della richiesta
 */
export const getFrontRichiesteIdRichiedente = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<Utente>(
    {
      url: `/framework-cmp-domande/richieste/${id}/richiedente`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetFrontRichiesteIdRichiedenteQueryKey = (id: string) =>
  [`/framework-cmp-domande/richieste/${id}/richiedente`] as const;

export const getGetFrontRichiesteIdRichiedenteQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteIdRichiedente>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteIdRichiedente>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontRichiesteIdRichiedente>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetFrontRichiesteIdRichiedenteQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontRichiesteIdRichiedente>>
  > = ({ signal }) =>
    getFrontRichiesteIdRichiedente(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetFrontRichiesteIdRichiedenteQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontRichiesteIdRichiedente>>
>;
export type GetFrontRichiesteIdRichiedenteQueryError = ErrorType<unknown>;

/**
 * @summary Recupero richiedente della richiesta
 */
export const useGetFrontRichiesteIdRichiedente = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteIdRichiedente>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteIdRichiedente>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontRichiesteIdRichiedenteQueryOptions(
    id,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Permette il download del pdf della ricevuta di pagamento
 * @summary Download del pdf della ricevuta di pagamento
 */
export const getFrontRichiesteIdPagamentiIdRicevuta = (
  id: string,
  idPagamento: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<string[]>(
    {
      url: `/framework-cmp-domande/richieste/${id}/pagamenti/${idPagamento}/ricevuta`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetFrontRichiesteIdPagamentiIdRicevutaQueryKey = (
  id: string,
  idPagamento: string,
) =>
  [
    `/framework-cmp-domande/richieste/${id}/pagamenti/${idPagamento}/ricevuta`,
  ] as const;

export const getGetFrontRichiesteIdPagamentiIdRicevutaQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteIdPagamentiIdRicevuta>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  idPagamento: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteIdPagamentiIdRicevuta>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontRichiesteIdPagamentiIdRicevuta>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetFrontRichiesteIdPagamentiIdRicevutaQueryKey(id, idPagamento);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontRichiesteIdPagamentiIdRicevuta>>
  > = ({ signal }) =>
    getFrontRichiesteIdPagamentiIdRicevuta(
      id,
      idPagamento,
      requestOptions,
      signal,
    );

  return { queryKey, queryFn, enabled: !!(id && idPagamento), ...queryOptions };
};

export type GetFrontRichiesteIdPagamentiIdRicevutaQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontRichiesteIdPagamentiIdRicevuta>>
>;
export type GetFrontRichiesteIdPagamentiIdRicevutaQueryError =
  ErrorType<unknown>;

/**
 * @summary Download del pdf della ricevuta di pagamento
 */
export const useGetFrontRichiesteIdPagamentiIdRicevuta = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteIdPagamentiIdRicevuta>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  idPagamento: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteIdPagamentiIdRicevuta>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontRichiesteIdPagamentiIdRicevutaQueryOptions(
    id,
    idPagamento,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Permette il download del pdf dell'avviso di pagamento
 * @summary Download del pdf dell'avviso di pagamento (Modello 3)
 */
export const getFrontRichiesteIdPagamentiIdAvviso = (
  id: string,
  idPagamento: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<string[]>(
    {
      url: `/framework-cmp-domande/richieste/${id}/pagamenti/${idPagamento}/avviso`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetFrontRichiesteIdPagamentiIdAvvisoQueryKey = (
  id: string,
  idPagamento: string,
) =>
  [
    `/framework-cmp-domande/richieste/${id}/pagamenti/${idPagamento}/avviso`,
  ] as const;

export const getGetFrontRichiesteIdPagamentiIdAvvisoQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteIdPagamentiIdAvviso>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  idPagamento: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteIdPagamentiIdAvviso>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontRichiesteIdPagamentiIdAvviso>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetFrontRichiesteIdPagamentiIdAvvisoQueryKey(id, idPagamento);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontRichiesteIdPagamentiIdAvviso>>
  > = ({ signal }) =>
    getFrontRichiesteIdPagamentiIdAvviso(
      id,
      idPagamento,
      requestOptions,
      signal,
    );

  return { queryKey, queryFn, enabled: !!(id && idPagamento), ...queryOptions };
};

export type GetFrontRichiesteIdPagamentiIdAvvisoQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontRichiesteIdPagamentiIdAvviso>>
>;
export type GetFrontRichiesteIdPagamentiIdAvvisoQueryError = ErrorType<unknown>;

/**
 * @summary Download del pdf dell'avviso di pagamento (Modello 3)
 */
export const useGetFrontRichiesteIdPagamentiIdAvviso = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteIdPagamentiIdAvviso>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  idPagamento: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteIdPagamentiIdAvviso>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontRichiesteIdPagamentiIdAvvisoQueryOptions(
    id,
    idPagamento,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Restituisce l’elenco di tutte le istanze di oneri associati a una domanda
 * @summary Recupero elenco oneri associati ad una domanda
 */
export const getFrontRichiesteIdOneri = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<OnereWithDettaglioDTO[]>(
    {
      url: `/framework-cmp-domande/richieste/${id}/oneri`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetFrontRichiesteIdOneriQueryKey = (id: string) =>
  [`/framework-cmp-domande/richieste/${id}/oneri`] as const;

export const getGetFrontRichiesteIdOneriQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteIdOneri>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteIdOneri>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontRichiesteIdOneri>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetFrontRichiesteIdOneriQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontRichiesteIdOneri>>
  > = ({ signal }) => getFrontRichiesteIdOneri(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetFrontRichiesteIdOneriQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontRichiesteIdOneri>>
>;
export type GetFrontRichiesteIdOneriQueryError = ErrorType<unknown>;

/**
 * @summary Recupero elenco oneri associati ad una domanda
 */
export const useGetFrontRichiesteIdOneri = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteIdOneri>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteIdOneri>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontRichiesteIdOneriQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Dettaglio richiesta modulo di una richiesta
 * @summary Dettaglio richiesta modulo di una richiesta
 */
export const getFrontRichiesteIdModuloIdRichiestamodulo = (
  id: string,
  idModulo: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<RichiestaModuloDTO>(
    {
      url: `/framework-cmp-domande/richieste/${id}/modulo/${idModulo}/richiestamodulo`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetFrontRichiesteIdModuloIdRichiestamoduloQueryKey = (
  id: string,
  idModulo: string,
) =>
  [
    `/framework-cmp-domande/richieste/${id}/modulo/${idModulo}/richiestamodulo`,
  ] as const;

export const getGetFrontRichiesteIdModuloIdRichiestamoduloQueryOptions = <
  TData = Awaited<
    ReturnType<typeof getFrontRichiesteIdModuloIdRichiestamodulo>
  >,
  TError = ErrorType<unknown>,
>(
  id: string,
  idModulo: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteIdModuloIdRichiestamodulo>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontRichiesteIdModuloIdRichiestamodulo>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetFrontRichiesteIdModuloIdRichiestamoduloQueryKey(id, idModulo);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontRichiesteIdModuloIdRichiestamodulo>>
  > = ({ signal }) =>
    getFrontRichiesteIdModuloIdRichiestamodulo(
      id,
      idModulo,
      requestOptions,
      signal,
    );

  return { queryKey, queryFn, enabled: !!(id && idModulo), ...queryOptions };
};

export type GetFrontRichiesteIdModuloIdRichiestamoduloQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontRichiesteIdModuloIdRichiestamodulo>>
>;
export type GetFrontRichiesteIdModuloIdRichiestamoduloQueryError =
  ErrorType<unknown>;

/**
 * @summary Dettaglio richiesta modulo di una richiesta
 */
export const useGetFrontRichiesteIdModuloIdRichiestamodulo = <
  TData = Awaited<
    ReturnType<typeof getFrontRichiesteIdModuloIdRichiestamodulo>
  >,
  TError = ErrorType<unknown>,
>(
  id: string,
  idModulo: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteIdModuloIdRichiestamodulo>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions =
    getGetFrontRichiesteIdModuloIdRichiestamoduloQueryOptions(
      id,
      idModulo,
      options,
    );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Moduli associati ad una richiesta
 * @summary Moduli di una richiesta
 */
export const getFrontRichiesteIdModuli = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<RichiestaModuloDTO[]>(
    {
      url: `/framework-cmp-domande/richieste/${id}/moduli`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetFrontRichiesteIdModuliQueryKey = (id: string) =>
  [`/framework-cmp-domande/richieste/${id}/moduli`] as const;

export const getGetFrontRichiesteIdModuliQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteIdModuli>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteIdModuli>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontRichiesteIdModuli>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetFrontRichiesteIdModuliQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontRichiesteIdModuli>>
  > = ({ signal }) => getFrontRichiesteIdModuli(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetFrontRichiesteIdModuliQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontRichiesteIdModuli>>
>;
export type GetFrontRichiesteIdModuliQueryError = ErrorType<unknown>;

/**
 * @summary Moduli di una richiesta
 */
export const useGetFrontRichiesteIdModuli = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteIdModuli>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteIdModuli>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontRichiesteIdModuliQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Dettaglio modulo di una richiesta
 * @summary Dettaglio modulo di una richiesta
 */
export const getFrontRichiesteIdModuliId = (
  id: string,
  idRichiestaModulo: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<RichiestaModuloDTO>(
    {
      url: `/framework-cmp-domande/richieste/${id}/moduli/${idRichiestaModulo}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetFrontRichiesteIdModuliIdQueryKey = (
  id: string,
  idRichiestaModulo: string,
) =>
  [
    `/framework-cmp-domande/richieste/${id}/moduli/${idRichiestaModulo}`,
  ] as const;

export const getGetFrontRichiesteIdModuliIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteIdModuliId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  idRichiestaModulo: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteIdModuliId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontRichiesteIdModuliId>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetFrontRichiesteIdModuliIdQueryKey(id, idRichiestaModulo);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontRichiesteIdModuliId>>
  > = ({ signal }) =>
    getFrontRichiesteIdModuliId(id, idRichiestaModulo, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!(id && idRichiestaModulo),
    ...queryOptions,
  };
};

export type GetFrontRichiesteIdModuliIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontRichiesteIdModuliId>>
>;
export type GetFrontRichiesteIdModuliIdQueryError = ErrorType<unknown>;

/**
 * @summary Dettaglio modulo di una richiesta
 */
export const useGetFrontRichiesteIdModuliId = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteIdModuliId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  idRichiestaModulo: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteIdModuliId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontRichiesteIdModuliIdQueryOptions(
    id,
    idRichiestaModulo,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Ottiene l'elenco delle richieste integrate
 * @summary Elenco richieste integrate
 */
export const getFrontRichiesteIdIntegrate = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<RichiestaDTO[]>(
    {
      url: `/framework-cmp-domande/richieste/${id}/integrate`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetFrontRichiesteIdIntegrateQueryKey = (id: string) =>
  [`/framework-cmp-domande/richieste/${id}/integrate`] as const;

export const getGetFrontRichiesteIdIntegrateQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteIdIntegrate>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteIdIntegrate>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontRichiesteIdIntegrate>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetFrontRichiesteIdIntegrateQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontRichiesteIdIntegrate>>
  > = ({ signal }) => getFrontRichiesteIdIntegrate(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetFrontRichiesteIdIntegrateQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontRichiesteIdIntegrate>>
>;
export type GetFrontRichiesteIdIntegrateQueryError = ErrorType<unknown>;

/**
 * @summary Elenco richieste integrate
 */
export const useGetFrontRichiesteIdIntegrate = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteIdIntegrate>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteIdIntegrate>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontRichiesteIdIntegrateQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Recupera le competenze associate al tipo domanda di una richiesta
 * @summary Recupera le competenze associate al tipo domanda di una richiesta
 */
export const getFrontRichiesteIdCompetenze = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<Competenza[]>(
    {
      url: `/framework-cmp-domande/richieste/${id}/competenze`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetFrontRichiesteIdCompetenzeQueryKey = (id: string) =>
  [`/framework-cmp-domande/richieste/${id}/competenze`] as const;

export const getGetFrontRichiesteIdCompetenzeQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteIdCompetenze>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteIdCompetenze>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontRichiesteIdCompetenze>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetFrontRichiesteIdCompetenzeQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontRichiesteIdCompetenze>>
  > = ({ signal }) => getFrontRichiesteIdCompetenze(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetFrontRichiesteIdCompetenzeQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontRichiesteIdCompetenze>>
>;
export type GetFrontRichiesteIdCompetenzeQueryError = ErrorType<unknown>;

/**
 * @summary Recupera le competenze associate al tipo domanda di una richiesta
 */
export const useGetFrontRichiesteIdCompetenze = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteIdCompetenze>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteIdCompetenze>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontRichiesteIdCompetenzeQueryOptions(
    id,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Ottiene l'elenco dei collegamenti di una richiesta
 * @summary Elenco collegamenti di una richiesta
 */
export const getFrontRichiesteIdCollegamenti = (
  id: string,
  params?: GetFrontRichiesteIdCollegamentiParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<CollegamentoRichiestaDTO[]>(
    {
      url: `/framework-cmp-domande/richieste/${id}/collegamenti`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetFrontRichiesteIdCollegamentiQueryKey = (
  id: string,
  params?: GetFrontRichiesteIdCollegamentiParams,
) =>
  [
    `/framework-cmp-domande/richieste/${id}/collegamenti`,
    ...(params ? [params] : []),
  ] as const;

export const getGetFrontRichiesteIdCollegamentiQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteIdCollegamenti>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  params?: GetFrontRichiesteIdCollegamentiParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteIdCollegamenti>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontRichiesteIdCollegamenti>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetFrontRichiesteIdCollegamentiQueryKey(id, params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontRichiesteIdCollegamenti>>
  > = ({ signal }) =>
    getFrontRichiesteIdCollegamenti(id, params, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetFrontRichiesteIdCollegamentiQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontRichiesteIdCollegamenti>>
>;
export type GetFrontRichiesteIdCollegamentiQueryError = ErrorType<unknown>;

/**
 * @summary Elenco collegamenti di una richiesta
 */
export const useGetFrontRichiesteIdCollegamenti = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteIdCollegamenti>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  params?: GetFrontRichiesteIdCollegamentiParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteIdCollegamenti>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontRichiesteIdCollegamentiQueryOptions(
    id,
    params,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Recupera i dettagli sugli allegati di una richiesta
 * @summary Recupera i dettagli sugli allegati di una richiesta
 */
export const getFrontRichiesteIdAllegati = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<AllegatoRichiestaDTO[]>(
    {
      url: `/framework-cmp-domande/richieste/${id}/allegati`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetFrontRichiesteIdAllegatiQueryKey = (id: string) =>
  [`/framework-cmp-domande/richieste/${id}/allegati`] as const;

export const getGetFrontRichiesteIdAllegatiQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteIdAllegati>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteIdAllegati>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontRichiesteIdAllegati>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetFrontRichiesteIdAllegatiQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontRichiesteIdAllegati>>
  > = ({ signal }) => getFrontRichiesteIdAllegati(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetFrontRichiesteIdAllegatiQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontRichiesteIdAllegati>>
>;
export type GetFrontRichiesteIdAllegatiQueryError = ErrorType<unknown>;

/**
 * @summary Recupera i dettagli sugli allegati di una richiesta
 */
export const useGetFrontRichiesteIdAllegati = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteIdAllegati>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteIdAllegati>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontRichiesteIdAllegatiQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const getFrontRichiesteIdIsDeletable = (
  idTagSemantico: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<boolean>(
    {
      url: `/framework-cmp-domande/richieste/${idTagSemantico}/isDeletable`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetFrontRichiesteIdIsDeletableQueryKey = (
  idTagSemantico: string,
) =>
  [`/framework-cmp-domande/richieste/${idTagSemantico}/isDeletable`] as const;

export const getGetFrontRichiesteIdIsDeletableQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteIdIsDeletable>>,
  TError = ErrorType<unknown>,
>(
  idTagSemantico: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteIdIsDeletable>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontRichiesteIdIsDeletable>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetFrontRichiesteIdIsDeletableQueryKey(idTagSemantico);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontRichiesteIdIsDeletable>>
  > = ({ signal }) =>
    getFrontRichiesteIdIsDeletable(idTagSemantico, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!idTagSemantico, ...queryOptions };
};

export type GetFrontRichiesteIdIsDeletableQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontRichiesteIdIsDeletable>>
>;
export type GetFrontRichiesteIdIsDeletableQueryError = ErrorType<unknown>;

export const useGetFrontRichiesteIdIsDeletable = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteIdIsDeletable>>,
  TError = ErrorType<unknown>,
>(
  idTagSemantico: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteIdIsDeletable>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontRichiesteIdIsDeletableQueryOptions(
    idTagSemantico,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Unisce i pdf di tutti i moduli della richiesta in un unico documento, ordinati per data creazione. Per ogni modulo utilizza la versione più avanzata disponibile (protocollato > firmato > compilato > generato). Se la richiesta non è in bozza il pdf viene salvato su repository e restituito dalla cache nelle chiamate successive.
 * @summary Download del pdf consolidato dell’intera richiesta (merge di tutti i moduli)
 */
export const getFrontRichiesteIdDocumentoConsolidato = (
  idRichiesta: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<string[]>(
    {
      url: `/framework-cmp-domande/richieste/${idRichiesta}/documento-consolidato`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetFrontRichiesteIdDocumentoConsolidatoQueryKey = (
  idRichiesta: string,
) =>
  [
    `/framework-cmp-domande/richieste/${idRichiesta}/documento-consolidato`,
  ] as const;

export const getGetFrontRichiesteIdDocumentoConsolidatoQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteIdDocumentoConsolidato>>,
  TError = ErrorType<unknown>,
>(
  idRichiesta: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteIdDocumentoConsolidato>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontRichiesteIdDocumentoConsolidato>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetFrontRichiesteIdDocumentoConsolidatoQueryKey(idRichiesta);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontRichiesteIdDocumentoConsolidato>>
  > = ({ signal }) =>
    getFrontRichiesteIdDocumentoConsolidato(
      idRichiesta,
      requestOptions,
      signal,
    );

  return { queryKey, queryFn, enabled: !!idRichiesta, ...queryOptions };
};

export type GetFrontRichiesteIdDocumentoConsolidatoQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontRichiesteIdDocumentoConsolidato>>
>;
export type GetFrontRichiesteIdDocumentoConsolidatoQueryError =
  ErrorType<unknown>;

/**
 * @summary Download del pdf consolidato dell’intera richiesta (merge di tutti i moduli)
 */
export const useGetFrontRichiesteIdDocumentoConsolidato = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteIdDocumentoConsolidato>>,
  TError = ErrorType<unknown>,
>(
  idRichiesta: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteIdDocumentoConsolidato>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontRichiesteIdDocumentoConsolidatoQueryOptions(
    idRichiesta,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * @summary Scarica un immagine del tipo richiesto
 */
export const getFrontRichiesteIdImmaginiId = (
  idRichiesta: string,
  idAllegato: string,
  params?: GetFrontRichiesteIdImmaginiIdParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<Blob>(
    {
      url: `/framework-cmp-domande/richieste/${idRichiesta}/immagini/${idAllegato}`,
      method: 'get',
      params,
      responseType: 'blob',
      signal,
    },
    options,
  );
};

export const getGetFrontRichiesteIdImmaginiIdQueryKey = (
  idRichiesta: string,
  idAllegato: string,
  params?: GetFrontRichiesteIdImmaginiIdParams,
) =>
  [
    `/framework-cmp-domande/richieste/${idRichiesta}/immagini/${idAllegato}`,
    ...(params ? [params] : []),
  ] as const;

export const getGetFrontRichiesteIdImmaginiIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteIdImmaginiId>>,
  TError = ErrorType<unknown>,
>(
  idRichiesta: string,
  idAllegato: string,
  params?: GetFrontRichiesteIdImmaginiIdParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteIdImmaginiId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontRichiesteIdImmaginiId>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetFrontRichiesteIdImmaginiIdQueryKey(idRichiesta, idAllegato, params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontRichiesteIdImmaginiId>>
  > = ({ signal }) =>
    getFrontRichiesteIdImmaginiId(
      idRichiesta,
      idAllegato,
      params,
      requestOptions,
      signal,
    );

  return {
    queryKey,
    queryFn,
    enabled: !!(idRichiesta && idAllegato),
    ...queryOptions,
  };
};

export type GetFrontRichiesteIdImmaginiIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontRichiesteIdImmaginiId>>
>;
export type GetFrontRichiesteIdImmaginiIdQueryError = ErrorType<unknown>;

/**
 * @summary Scarica un immagine del tipo richiesto
 */
export const useGetFrontRichiesteIdImmaginiId = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteIdImmaginiId>>,
  TError = ErrorType<unknown>,
>(
  idRichiesta: string,
  idAllegato: string,
  params?: GetFrontRichiesteIdImmaginiIdParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteIdImmaginiId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontRichiesteIdImmaginiIdQueryOptions(
    idRichiesta,
    idAllegato,
    params,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const getFrontRichiesteXlsx = (
  params?: GetFrontRichiesteXlsxParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<string[]>(
    {
      url: `/framework-cmp-domande/richieste/xlsx`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetFrontRichiesteXlsxQueryKey = (
  params?: GetFrontRichiesteXlsxParams,
) =>
  [
    `/framework-cmp-domande/richieste/xlsx`,
    ...(params ? [params] : []),
  ] as const;

export const getGetFrontRichiesteXlsxQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteXlsx>>,
  TError = ErrorType<unknown>,
>(
  params?: GetFrontRichiesteXlsxParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteXlsx>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontRichiesteXlsx>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetFrontRichiesteXlsxQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontRichiesteXlsx>>
  > = ({ signal }) => getFrontRichiesteXlsx(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetFrontRichiesteXlsxQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontRichiesteXlsx>>
>;
export type GetFrontRichiesteXlsxQueryError = ErrorType<unknown>;

export const useGetFrontRichiesteXlsx = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteXlsx>>,
  TError = ErrorType<unknown>,
>(
  params?: GetFrontRichiesteXlsxParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteXlsx>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontRichiesteXlsxQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Recupero dei dati di ogni sezione per ogni modulo relativo ad una richiesta
 */
export const getFrontRichiesteValoriSezioniProcedimentoId = (
  idIstanzaProcedimento: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<
    GetFrontRichiesteValoriSezioniProcedimentoId200Item[]
  >(
    {
      url: `/framework-cmp-domande/richieste/valori-sezioni/procedimento/${idIstanzaProcedimento}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetFrontRichiesteValoriSezioniProcedimentoIdQueryKey = (
  idIstanzaProcedimento: string,
) =>
  [
    `/framework-cmp-domande/richieste/valori-sezioni/procedimento/${idIstanzaProcedimento}`,
  ] as const;

export const getGetFrontRichiesteValoriSezioniProcedimentoIdQueryOptions = <
  TData = Awaited<
    ReturnType<typeof getFrontRichiesteValoriSezioniProcedimentoId>
  >,
  TError = ErrorType<unknown>,
>(
  idIstanzaProcedimento: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteValoriSezioniProcedimentoId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontRichiesteValoriSezioniProcedimentoId>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetFrontRichiesteValoriSezioniProcedimentoIdQueryKey(
      idIstanzaProcedimento,
    );

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontRichiesteValoriSezioniProcedimentoId>>
  > = ({ signal }) =>
    getFrontRichiesteValoriSezioniProcedimentoId(
      idIstanzaProcedimento,
      requestOptions,
      signal,
    );

  return {
    queryKey,
    queryFn,
    enabled: !!idIstanzaProcedimento,
    ...queryOptions,
  };
};

export type GetFrontRichiesteValoriSezioniProcedimentoIdQueryResult =
  NonNullable<
    Awaited<ReturnType<typeof getFrontRichiesteValoriSezioniProcedimentoId>>
  >;
export type GetFrontRichiesteValoriSezioniProcedimentoIdQueryError =
  ErrorType<unknown>;

export const useGetFrontRichiesteValoriSezioniProcedimentoId = <
  TData = Awaited<
    ReturnType<typeof getFrontRichiesteValoriSezioniProcedimentoId>
  >,
  TError = ErrorType<unknown>,
>(
  idIstanzaProcedimento: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteValoriSezioniProcedimentoId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions =
    getGetFrontRichiesteValoriSezioniProcedimentoIdQueryOptions(
      idIstanzaProcedimento,
      options,
    );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Recupero delle mappe geografiche di ogni sezione per ogni modulo relativo ad una richiesta
 */
export const getFrontRichiesteMappeSezioniProcedimentoId = (
  idIstanzaProcedimento: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<MappaGeografica[]>(
    {
      url: `/framework-cmp-domande/richieste/mappe-sezioni/procedimento/${idIstanzaProcedimento}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetFrontRichiesteMappeSezioniProcedimentoIdQueryKey = (
  idIstanzaProcedimento: string,
) =>
  [
    `/framework-cmp-domande/richieste/mappe-sezioni/procedimento/${idIstanzaProcedimento}`,
  ] as const;

export const getGetFrontRichiesteMappeSezioniProcedimentoIdQueryOptions = <
  TData = Awaited<
    ReturnType<typeof getFrontRichiesteMappeSezioniProcedimentoId>
  >,
  TError = ErrorType<unknown>,
>(
  idIstanzaProcedimento: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteMappeSezioniProcedimentoId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontRichiesteMappeSezioniProcedimentoId>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetFrontRichiesteMappeSezioniProcedimentoIdQueryKey(
      idIstanzaProcedimento,
    );

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontRichiesteMappeSezioniProcedimentoId>>
  > = ({ signal }) =>
    getFrontRichiesteMappeSezioniProcedimentoId(
      idIstanzaProcedimento,
      requestOptions,
      signal,
    );

  return {
    queryKey,
    queryFn,
    enabled: !!idIstanzaProcedimento,
    ...queryOptions,
  };
};

export type GetFrontRichiesteMappeSezioniProcedimentoIdQueryResult =
  NonNullable<
    Awaited<ReturnType<typeof getFrontRichiesteMappeSezioniProcedimentoId>>
  >;
export type GetFrontRichiesteMappeSezioniProcedimentoIdQueryError =
  ErrorType<unknown>;

export const useGetFrontRichiesteMappeSezioniProcedimentoId = <
  TData = Awaited<
    ReturnType<typeof getFrontRichiesteMappeSezioniProcedimentoId>
  >,
  TError = ErrorType<unknown>,
>(
  idIstanzaProcedimento: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteMappeSezioniProcedimentoId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions =
    getGetFrontRichiesteMappeSezioniProcedimentoIdQueryOptions(
      idIstanzaProcedimento,
      options,
    );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Recupera i dettagli sugli allegati richiesta di un Istanza Procedimento
 * @summary Recupera i dettagli sugli allegati richiesta di un Istanza Procedimento
 */
export const getFrontRichiesteIstanzaProcedimentoIdAllegati = (
  idIstanzaProcedimento: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<AllegatoRichiestaDTO[]>(
    {
      url: `/framework-cmp-domande/richieste/istanza-procedimento/${idIstanzaProcedimento}/allegati`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetFrontRichiesteIstanzaProcedimentoIdAllegatiQueryKey = (
  idIstanzaProcedimento: string,
) =>
  [
    `/framework-cmp-domande/richieste/istanza-procedimento/${idIstanzaProcedimento}/allegati`,
  ] as const;

export const getGetFrontRichiesteIstanzaProcedimentoIdAllegatiQueryOptions = <
  TData = Awaited<
    ReturnType<typeof getFrontRichiesteIstanzaProcedimentoIdAllegati>
  >,
  TError = ErrorType<unknown>,
>(
  idIstanzaProcedimento: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<
        ReturnType<typeof getFrontRichiesteIstanzaProcedimentoIdAllegati>
      >,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontRichiesteIstanzaProcedimentoIdAllegati>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetFrontRichiesteIstanzaProcedimentoIdAllegatiQueryKey(
      idIstanzaProcedimento,
    );

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontRichiesteIstanzaProcedimentoIdAllegati>>
  > = ({ signal }) =>
    getFrontRichiesteIstanzaProcedimentoIdAllegati(
      idIstanzaProcedimento,
      requestOptions,
      signal,
    );

  return {
    queryKey,
    queryFn,
    enabled: !!idIstanzaProcedimento,
    ...queryOptions,
  };
};

export type GetFrontRichiesteIstanzaProcedimentoIdAllegatiQueryResult =
  NonNullable<
    Awaited<ReturnType<typeof getFrontRichiesteIstanzaProcedimentoIdAllegati>>
  >;
export type GetFrontRichiesteIstanzaProcedimentoIdAllegatiQueryError =
  ErrorType<unknown>;

/**
 * @summary Recupera i dettagli sugli allegati richiesta di un Istanza Procedimento
 */
export const useGetFrontRichiesteIstanzaProcedimentoIdAllegati = <
  TData = Awaited<
    ReturnType<typeof getFrontRichiesteIstanzaProcedimentoIdAllegati>
  >,
  TError = ErrorType<unknown>,
>(
  idIstanzaProcedimento: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<
        ReturnType<typeof getFrontRichiesteIstanzaProcedimentoIdAllegati>
      >,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions =
    getGetFrontRichiesteIstanzaProcedimentoIdAllegatiQueryOptions(
      idIstanzaProcedimento,
      options,
    );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const getFrontRichiesteGeojson = (
  params?: GetFrontRichiesteGeojsonParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<string>(
    {
      url: `/framework-cmp-domande/richieste/geojson`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetFrontRichiesteGeojsonQueryKey = (
  params?: GetFrontRichiesteGeojsonParams,
) =>
  [
    `/framework-cmp-domande/richieste/geojson`,
    ...(params ? [params] : []),
  ] as const;

export const getGetFrontRichiesteGeojsonQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteGeojson>>,
  TError = ErrorType<unknown>,
>(
  params?: GetFrontRichiesteGeojsonParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteGeojson>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontRichiesteGeojson>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetFrontRichiesteGeojsonQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontRichiesteGeojson>>
  > = ({ signal }) => getFrontRichiesteGeojson(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetFrontRichiesteGeojsonQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontRichiesteGeojson>>
>;
export type GetFrontRichiesteGeojsonQueryError = ErrorType<unknown>;

export const useGetFrontRichiesteGeojson = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteGeojson>>,
  TError = ErrorType<unknown>,
>(
  params?: GetFrontRichiesteGeojsonParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteGeojson>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontRichiesteGeojsonQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Recupero tutti i campi potenzialmente estraibili in geogson per tutti i tipi di richiesta/domanda (per verticale)
 */
export const getFrontRichiesteGeojsonCampi = (
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<CampoEstraibileCSV[]>(
    {
      url: `/framework-cmp-domande/richieste/geojson/campi`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetFrontRichiesteGeojsonCampiQueryKey = () =>
  [`/framework-cmp-domande/richieste/geojson/campi`] as const;

export const getGetFrontRichiesteGeojsonCampiQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteGeojsonCampi>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getFrontRichiesteGeojsonCampi>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontRichiesteGeojsonCampi>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetFrontRichiesteGeojsonCampiQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontRichiesteGeojsonCampi>>
  > = ({ signal }) => getFrontRichiesteGeojsonCampi(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetFrontRichiesteGeojsonCampiQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontRichiesteGeojsonCampi>>
>;
export type GetFrontRichiesteGeojsonCampiQueryError = ErrorType<unknown>;

export const useGetFrontRichiesteGeojsonCampi = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteGeojsonCampi>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getFrontRichiesteGeojsonCampi>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontRichiesteGeojsonCampiQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const getFrontRichiesteCsv = (
  params?: GetFrontRichiesteCsvParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<string[]>(
    {
      url: `/framework-cmp-domande/richieste/csv`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetFrontRichiesteCsvQueryKey = (
  params?: GetFrontRichiesteCsvParams,
) =>
  [
    `/framework-cmp-domande/richieste/csv`,
    ...(params ? [params] : []),
  ] as const;

export const getGetFrontRichiesteCsvQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteCsv>>,
  TError = ErrorType<unknown>,
>(
  params?: GetFrontRichiesteCsvParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteCsv>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontRichiesteCsv>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetFrontRichiesteCsvQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontRichiesteCsv>>
  > = ({ signal }) => getFrontRichiesteCsv(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetFrontRichiesteCsvQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontRichiesteCsv>>
>;
export type GetFrontRichiesteCsvQueryError = ErrorType<unknown>;

export const useGetFrontRichiesteCsv = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteCsv>>,
  TError = ErrorType<unknown>,
>(
  params?: GetFrontRichiesteCsvParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteCsv>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontRichiesteCsvQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Recupero tutti i campi potenzialmente estraibili in csv per tutti i tipi di richiesta/domanda (per verticale)
 */
export const getFrontRichiesteCsvCampi = (
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<CampoEstraibileCSV[]>(
    {
      url: `/framework-cmp-domande/richieste/csv/campi`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetFrontRichiesteCsvCampiQueryKey = () =>
  [`/framework-cmp-domande/richieste/csv/campi`] as const;

export const getGetFrontRichiesteCsvCampiQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteCsvCampi>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getFrontRichiesteCsvCampi>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontRichiesteCsvCampi>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetFrontRichiesteCsvCampiQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontRichiesteCsvCampi>>
  > = ({ signal }) => getFrontRichiesteCsvCampi(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetFrontRichiesteCsvCampiQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontRichiesteCsvCampi>>
>;
export type GetFrontRichiesteCsvCampiQueryError = ErrorType<unknown>;

export const useGetFrontRichiesteCsvCampi = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteCsvCampi>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getFrontRichiesteCsvCampi>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontRichiesteCsvCampiQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Dettaglio della richiesta, visibile solo da un utente che è compilatore e/o delagato della richiestas
 * @summary Dettaglio richiesta
 */
export const getFrontRichiesteByIdIstanzaProcedimentoId = (
  idIstanzaProcedimento: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<RichiestaDTO>(
    {
      url: `/framework-cmp-domande/richieste/byIdIstanzaProcedimento/${idIstanzaProcedimento}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetFrontRichiesteByIdIstanzaProcedimentoIdQueryKey = (
  idIstanzaProcedimento: string,
) =>
  [
    `/framework-cmp-domande/richieste/byIdIstanzaProcedimento/${idIstanzaProcedimento}`,
  ] as const;

export const getGetFrontRichiesteByIdIstanzaProcedimentoIdQueryOptions = <
  TData = Awaited<
    ReturnType<typeof getFrontRichiesteByIdIstanzaProcedimentoId>
  >,
  TError = ErrorType<unknown>,
>(
  idIstanzaProcedimento: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteByIdIstanzaProcedimentoId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontRichiesteByIdIstanzaProcedimentoId>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetFrontRichiesteByIdIstanzaProcedimentoIdQueryKey(
      idIstanzaProcedimento,
    );

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontRichiesteByIdIstanzaProcedimentoId>>
  > = ({ signal }) =>
    getFrontRichiesteByIdIstanzaProcedimentoId(
      idIstanzaProcedimento,
      requestOptions,
      signal,
    );

  return {
    queryKey,
    queryFn,
    enabled: !!idIstanzaProcedimento,
    ...queryOptions,
  };
};

export type GetFrontRichiesteByIdIstanzaProcedimentoIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontRichiesteByIdIstanzaProcedimentoId>>
>;
export type GetFrontRichiesteByIdIstanzaProcedimentoIdQueryError =
  ErrorType<unknown>;

/**
 * @summary Dettaglio richiesta
 */
export const useGetFrontRichiesteByIdIstanzaProcedimentoId = <
  TData = Awaited<
    ReturnType<typeof getFrontRichiesteByIdIstanzaProcedimentoId>
  >,
  TError = ErrorType<unknown>,
>(
  idIstanzaProcedimento: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteByIdIstanzaProcedimentoId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions =
    getGetFrontRichiesteByIdIstanzaProcedimentoIdQueryOptions(
      idIstanzaProcedimento,
      options,
    );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
